import React, { useEffect, useMemo, useRef, useState, createContext } from "react";
import ReactECharts from "echarts-for-react";
import { SketchPicker } from "react-color";

/** Context: exposes internal state to wrapper UIs (e.g. ForecastDashboardDecisionUI) */
export const ForecastDashboardContext = createContext(null);

/**
 * ForecastDashboard.js
 * - Single-file version of api.ts + App.tsx (JS only)
 * - Admin-gated (UserID=admin)
 * - Calls staged overlay backend at: assent.ogpumper.net/api/overlay_staged.php
 *
 * Dependencies:
 *   npm i echarts echarts-for-react
 */

// ---------- API base (IMPORTANT: avoid mixed-content on https) ----------
function resolveApiBase() {
  // Optional explicit overrides (highest priority):
  // 1) window.__FORECAST_API_BASE__ = "https://custom-host"
  // 2) process.env.REACT_APP_FORECAST_API_BASE (CRA-style)
  const win = window.__FORECAST_API_BASE__;
  const env =
    (typeof process !== "undefined" &&
      process?.env?.REACT_APP_FORECAST_API_BASE) ||
    "";

  if (win || env) return String(win || env).trim();

  // Multi-tenant: derive from ?Company= query param (same model as LEASES_API/TAGS_API)
  try {
    const qs = new URLSearchParams(window.location.search);
    const company = (qs.get("Company") || "").trim().toLowerCase();
    if (company && /^[a-z0-9_-]{1,64}$/.test(company)) {
      return `https://${company}.ogpumper.net`;
    }
  } catch {}

  // Fallback when no Company param is present
  return "https://assent.ogpumper.net";
}

// ---------- Constants & helpers ----------
const STREAMS = ["Oil", "Gas", "Water"];
const COLORS = {
  Oil: "#22c55e", // green
  Gas: "#ef4444", // red
  Water: "#3b82f6", // blue
};
const FD_SERIES_KINDS = [
  { key: "Actual", label: "Actual" },
  { key: "Forecast(p50)", label: "Forecast (p50)" },
  { key: "User Forecast(p50)", label: "User Forecast (p50)" },
  { key: "P10", label: "P10" },
  { key: "P90", label: "P90" },
  { key: "MA", label: "Moving Avg" },
  { key: "Outliers", label: "Outliers" },
  { key: "Anomalies", label: "Anomalies" },
];

function defaultForecastDashPrefs() {
  const mk = (c) => ({
    Actual: { render: "scatter", symbol: "circle", symbolSize: 5 },
    MA: {
      render: "line",
      width: 2,
      dash: "solid",
      smooth: true,
      opacity: 0.95,
    },
    "Forecast(p50)": {
      render: "line",
      width: 2,
      dash: "dashed",
      smooth: false,
      opacity: 0.95,
    },
    "User Forecast(p50)": {
      render: "line",
      width: 2.5,
      dash: "solid",
      smooth: false,
      opacity: 0.95,
    },
    P10: {
      render: "line",
      width: 1.5,
      dash: "dotted",
      smooth: false,
      opacity: 0.55,
    },
    P90: {
      render: "line",
      width: 1.5,
      dash: "dotted",
      smooth: false,
      opacity: 0.55,
    },
    Outliers: {
      render: "scatter",
      color: "#f59e0b",
      symbol: "triangle",
      symbolSize: 9,
    },
    Anomalies: {
      render: "scatter",
      color: "#dc2626",
      symbol: "pin",
      symbolSize: 14,
    },
  });
  return {
    colors: { ...COLORS },
    seriesPrefs: {
      Oil: mk(COLORS.Oil),
      Gas: mk(COLORS.Gas),
      Water: mk(COLORS.Water),
    },
    bandFillAlphaLight: 0.16,
    bandFillAlphaDark: 0.18,
  };
}

async function fetchChartsPrefUserDetails({ apiBase, userId, passThrough }) {
  const url = `${apiBase}/api/userdetails.php?id=${encodeURIComponent(userId)}&chartsPref=1`;
  const res = await fetch(url, {
    headers: {
      Accept: "application/json",
      ...authHeadersFromPassThrough(passThrough),
    },
  });
  const txt = await res.text().catch(() => "");
  if (!res.ok)
    throw new Error(`UserDetails GET ${res.status}: ${txt || res.statusText}`);
  const json = txt ? JSON.parse(txt) : {};
  return json?.ChartsPref || null;
}

async function patchChartsPrefUserDetails({
  apiBase,
  userId,
  passThrough,
  chartsPrefAll,
}) {
  const url = `${apiBase}/api/userdetails.php`;
  const res = await fetch(url, {
    method: "PATCH",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...authHeadersFromPassThrough(passThrough),
    },
    body: JSON.stringify({ UserID: userId, ChartsPref: chartsPrefAll }),
  });
  const txt = await res.text().catch(() => "");
  if (!res.ok)
    throw new Error(
      `UserDetails PATCH ${res.status}: ${txt || res.statusText}`,
    );
  return txt ? JSON.parse(txt) : {};
}

const fmt = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
const fmt0 = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const pct = new Intl.NumberFormat(undefined, {
  style: "percent",
  maximumFractionDigits: 1,
});
const iso = (d) => d.toISOString().slice(0, 10);

function parseISO(s) {
  return new Date(s + (s && s.length === 10 ? "T00:00:00Z" : ""));
}
function definedNum(n) {
  return Number.isFinite(n);
}
function toPair(p) {
  return definedNum(p?.q) ? [p.date, p.q] : null;
}
function clamp(n, lo, hi) {
  const v = Number(n);
  if (!Number.isFinite(v)) return lo;
  return Math.max(lo, Math.min(hi, v));
}
function clamp01(n) {
  return clamp(n, 0, 1);
}
function daysBetweenISO(a, b) {
  if (!a || !b) return null;
  const ta = +parseISO(a);
  const tb = +parseISO(b);
  if (!Number.isFinite(ta) || !Number.isFinite(tb)) return null;
  return Math.round((tb - ta) / 86400000);
}
function money(n, digits = 0) {
  if (!definedNum(n)) return "—";
  const nf = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: digits,
  });
  return nf.format(n);
}
function signColor(v, dark) {
  if (!definedNum(v)) return dark ? "#9ca3af" : "#6b7280";
  return v >= 0 ? (dark ? "#34d399" : "#059669") : dark ? "#f87171" : "#b91c1c";
}

function movingAverage(points, window = 7) {
  const arr = (points || [])
    .filter((p) => definedNum(p?.q))
    .map((p) => ({ t: +parseISO(p.date), d: p.date, v: p.q }))
    .sort((a, b) => a.t - b.t);

  const out = [];
  const q = [];
  for (let i = 0; i < arr.length; i++) {
    q.push(arr[i].v);
    if (q.length > window) q.shift();
    const avg = q.reduce((s, x) => s + x, 0) / q.length;
    out.push([arr[i].d, avg]);
  }
  return out;
}

function outlierDates(points) {
  const vals = (points || [])
    .filter((p) => definedNum(p?.q))
    .map((p) => p.q)
    .sort((a, b) => a - b);

  if (vals.length < 12) return new Set();
  const q1 = vals[Math.floor(vals.length * 0.25)];
  const q3 = vals[Math.floor(vals.length * 0.75)];
  const iqr = q3 - q1;
  const lo = q1 - 1.5 * iqr;
  const hi = q3 + 1.5 * iqr;

  return new Set(
    (points || [])
      .filter((p) => definedNum(p?.q) && (p.q < lo || p.q > hi))
      .map((p) => p.date),
  );
}

function latest(points) {
  if (!points || !points.length) return undefined;
  return [...points]
    .filter((p) => definedNum(p?.q))
    .sort((a, b) => +parseISO(b.date) - +parseISO(a.date))[0];
}

function averageOfLast(points, days = 7) {
  const sorted = [...(points || [])]
    .filter((p) => definedNum(p?.q))
    .sort((a, b) => +parseISO(b.date) - +parseISO(a.date));
  const tail = sorted.slice(0, days).map((p) => p.q);
  if (!tail.length) return null;
  return tail.reduce((s, x) => s + x, 0) / tail.length;
}

function csvEscape(s) {
  return /[",\n]/.test(s) ? `"${String(s).replace(/"/g, '""')}"` : String(s);
}

function hexToRgba(hex, alpha = 1) {
  const h = String(hex || "").replace("#", "");
  const bigint = parseInt(h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function cumulative(series) {
  let acc = 0;
  return [...(series || [])]
    .sort((a, b) => +parseISO(a.date) - +parseISO(b.date))
    .map((p) => {
      const v = definedNum(p?.q) ? p.q : 0;
      acc += v;
      return { date: p.date, q: acc };
    });
}

function isQuantilePointArray(entry) {
  if (!Array.isArray(entry) || entry.length === 0) return false;
  const x = entry[0];
  return (
    x &&
    typeof x === "object" &&
    "date" in x &&
    ("p50" in x || "p10" in x || "p90" in x || "mean" in x || "median" in x)
  );
}

function gatherAllDates(actual, forecast) {
  const dates = new Set();
  Object.values(actual || {}).forEach((arr) =>
    arr?.forEach?.((p) => dates.add(p.date)),
  );

  for (const s of STREAMS) {
    const f = forecast?.[s];
    if (!f) continue;

    if (Array.isArray(f)) {
      if (isQuantilePointArray(f)) f.forEach((p) => dates.add(p.date));
      else f.forEach((p) => dates.add(p.date));
    } else if (typeof f === "object") {
      (f.p10 || []).forEach((p) => dates.add(p.date));
      (f.p50 || f.median || f.mean || []).forEach((p) => dates.add(p.date));
      (f.p90 || []).forEach((p) => dates.add(p.date));
    }
  }

  return [...dates].sort((a, b) => +parseISO(a) - +parseISO(b));
}

function datesIntersection(a, b) {
  const mb = new Map((b || []).map(([d, v]) => [d, v]));
  const outA = [];
  const outB = [];
  for (const [d, va] of a || []) {
    if (mb.has(d)) {
      outA.push([d, va]);
      outB.push([d, mb.get(d)]);
    }
  }
  return { a: outA, b: outB };
}
function actualDomainFromActuals(actual) {
  const dates = [];
  for (const s of STREAMS) {
    (actual?.[s] || []).forEach((p) => {
      if (p?.date) dates.push(String(p.date));
    });
  }
  if (!dates.length) return { from: "", thru: "" };
  dates.sort((a, b) => +parseISO(a) - +parseISO(b));
  return { from: dates[0] || "", thru: dates[dates.length - 1] || "" };
}

function extractForecast(entry) {
  const empty = { mid: [], p10: [], p90: [], hasBand: false };
  if (!entry) return empty;

  // A) Array of Points [{date,q}]
  if (Array.isArray(entry) && !isQuantilePointArray(entry)) {
    return { ...empty, mid: entry.map(toPair).filter(Boolean) };
  }

  // B) quantile-per-point array [{date,p10,p50,p90}]
  if (Array.isArray(entry) && isQuantilePointArray(entry)) {
    const mid = entry
      .map((p) =>
        definedNum(p.p50 ?? p.median ?? p.mean)
          ? [p.date, Number(p.p50 ?? p.median ?? p.mean)]
          : null,
      )
      .filter(Boolean);
    const p10 = entry
      .map((p) => (definedNum(p.p10) ? [p.date, Number(p.p10)] : null))
      .filter(Boolean);
    const p90 = entry
      .map((p) => (definedNum(p.p90) ? [p.date, Number(p.p90)] : null))
      .filter(Boolean);
    return { mid, p10, p90, hasBand: p10.length > 0 && p90.length > 0 };
  }

  // C) bands-as-arrays { p10: Point[], p50: Point[], p90: Point[] }
  const obj = entry || {};
  const midSrc = obj.p50 || obj.median || obj.mean || [];
  const mid = (midSrc || []).map(toPair).filter(Boolean);
  const p10 = (obj.p10 || []).map(toPair).filter(Boolean);
  const p90 = (obj.p90 || []).map(toPair).filter(Boolean);
  return { mid, p10, p90, hasBand: p10.length > 0 && p90.length > 0 };
}

function withUnits(val, unit) {
  if (!definedNum(val)) return "—";
  return unit ? `${fmt.format(val)} ${unit}` : fmt.format(val);
}

function normalizeTagsParam(raw) {
  const v = String(raw || "").trim();
  if (!v) return [];
  const low = v.toLowerCase();
  if (low === "all" || low === "~all~") return [];
  return v
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/** CSV export that understands quantile-per-point arrays */
function toCSV(resp) {
  const allDates = new Set();

  STREAMS.forEach((s) => {
    (resp?.actual?.[s] || []).forEach((p) => allDates.add(p.date));
    const f = resp?.forecast?.[s];
    if (!f) return;

    if (Array.isArray(f)) {
      if (isQuantilePointArray(f)) f.forEach((p) => allDates.add(p.date));
      else f.forEach((p) => allDates.add(p.date));
    } else if (typeof f === "object") {
      (f.p10 || []).forEach((p) => allDates.add(p.date));
      (f.p50 || f.median || f.mean || []).forEach((p) => allDates.add(p.date));
      (f.p90 || []).forEach((p) => allDates.add(p.date));
    }
  });

  const dates = [...allDates].sort((a, b) => +parseISO(a) - +parseISO(b));
  const header = [
    "date",
    ...STREAMS.flatMap((s) => [`${s} Actual`, `${s} Forecast(p50)`]),
  ];
  const rows = [header.join(",")];

  for (const d of dates) {
    const row = [d];
    for (const s of STREAMS) {
      const a = (resp?.actual?.[s] || []).find((p) => p.date === d)?.q ?? "";

      const fRaw = resp?.forecast?.[s];
      let f = "";
      if (Array.isArray(fRaw)) {
        if (isQuantilePointArray(fRaw)) {
          const hit = fRaw.find((p) => p.date === d);
          if (hit) f = hit.p50 ?? hit.median ?? hit.mean ?? "";
        } else {
          f = (fRaw || []).find((p) => p.date === d)?.q ?? "";
        }
      } else if (fRaw && typeof fRaw === "object") {
        const p50 = fRaw.p50 || fRaw.median || fRaw.mean || [];
        f = (p50 || []).find((p) => p.date === d)?.q ?? "";
      }

      row.push(a, f);
    }
    rows.push(
      row.map((x) => (typeof x === "string" ? csvEscape(x) : x)).join(","),
    );
  }

  return rows.join("\n");
}

/* --------------------------------------------------------------------
 * CHART PREFS (users.ChartsPref) – Forecast Dashboard v2
 * - Stored server-side per user (users.ChartsPref JSON)
 * - MERGES on read/write so we never clobber existing prefs for other pages
 * -------------------------------------------------------------------- */

const FD_PREFS_VERSION = 2;

// Defaults: all series inherit stream base color unless pref.color is set
const DEFAULT_FD_PREFS = {
  colors: { ...COLORS },
  seriesPrefs: {
    Oil: {
      Actual: { render: "scatter", symbol: "circle", symbolSize: 5 },
      MA: {
        render: "line",
        width: 2,
        dash: "solid",
        smooth: true,
        opacity: 0.95,
      },
      "Forecast(p50)": {
        render: "line",
        width: 2,
        dash: "dashed",
        smooth: false,
        opacity: 0.95,
      },
      "User Forecast(p50)": {
        render: "line",
        width: 2.5,
        dash: "solid",
        smooth: false,
        opacity: 0.95,
      },
      P10: {
        render: "line",
        width: 1.5,
        dash: "dotted",
        smooth: false,
        opacity: 0.55,
      },
      P90: {
        render: "line",
        width: 1.5,
        dash: "dotted",
        smooth: false,
        opacity: 0.55,
      },
      Outliers: {
        render: "scatter",
        color: "#f59e0b",
        symbol: "triangle",
        symbolSize: 9,
      },
      Anomalies: { render: "scatter", color: "#dc2626", symbol: "pin" },
    },
    Gas: {
      Actual: { render: "scatter", symbol: "circle", symbolSize: 5 },
      MA: {
        render: "line",
        width: 2,
        dash: "solid",
        smooth: true,
        opacity: 0.95,
      },
      "Forecast(p50)": {
        render: "line",
        width: 2,
        dash: "dashed",
        smooth: false,
        opacity: 0.95,
      },
      "User Forecast(p50)": {
        render: "line",
        width: 2.5,
        dash: "solid",
        smooth: false,
        opacity: 0.95,
      },
      P10: {
        render: "line",
        width: 1.5,
        dash: "dotted",
        smooth: false,
        opacity: 0.55,
      },
      P90: {
        render: "line",
        width: 1.5,
        dash: "dotted",
        smooth: false,
        opacity: 0.55,
      },
      Outliers: {
        render: "scatter",
        color: "#f59e0b",
        symbol: "triangle",
        symbolSize: 9,
      },
      Anomalies: { render: "scatter", color: "#dc2626", symbol: "pin" },
    },
    Water: {
      Actual: { render: "scatter", symbol: "circle", symbolSize: 5 },
      MA: {
        render: "line",
        width: 2,
        dash: "solid",
        smooth: true,
        opacity: 0.95,
      },
      "Forecast(p50)": {
        render: "line",
        width: 2,
        dash: "dashed",
        smooth: false,
        opacity: 0.95,
      },
      "User Forecast(p50)": {
        render: "line",
        width: 2.5,
        dash: "solid",
        smooth: false,
        opacity: 0.95,
      },
      P10: {
        render: "line",
        width: 1.5,
        dash: "dotted",
        smooth: false,
        opacity: 0.55,
      },
      P90: {
        render: "line",
        width: 1.5,
        dash: "dotted",
        smooth: false,
        opacity: 0.55,
      },
      Outliers: {
        render: "scatter",
        color: "#f59e0b",
        symbol: "triangle",
        symbolSize: 9,
      },
      Anomalies: { render: "scatter", color: "#dc2626", symbol: "pin" },
    },
  },
  bandFillAlphaLight: 0.16,
  bandFillAlphaDark: 0.18,
};

function deepClone(obj) {
  return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}

function coerceSeriesPref(p) {
  if (!p || typeof p !== "object") return {};
  const out = { ...p };

  if (out.render && !["line", "bar", "scatter"].includes(out.render))
    delete out.render;
  if (out.dash && !["solid", "dashed", "dotted"].includes(out.dash))
    delete out.dash;
  if (out.width != null && !definedNum(out.width)) delete out.width;
  if (out.opacity != null && !definedNum(out.opacity)) delete out.opacity;
  if (out.symbolSize != null && !definedNum(out.symbolSize))
    delete out.symbolSize;
  if (out.barWidth != null && !definedNum(out.barWidth)) delete out.barWidth;
  if (out.smooth != null && typeof out.smooth !== "boolean") delete out.smooth;
  if (out.color != null && typeof out.color !== "string") delete out.color;
  if (out.symbol != null && typeof out.symbol !== "string") delete out.symbol;

  return out;
}

function mergeForecastDashPrefs(fd) {
  const base = fd && typeof fd === "object" ? deepClone(fd) : {};

  base.colors = { ...COLORS, ...(base.colors || {}) };
  base.seriesPrefs =
    base.seriesPrefs && typeof base.seriesPrefs === "object"
      ? base.seriesPrefs
      : {};

  for (const s of STREAMS) {
    const defS = DEFAULT_FD_PREFS.seriesPrefs[s] || {};
    const gotS =
      base.seriesPrefs[s] && typeof base.seriesPrefs[s] === "object"
        ? base.seriesPrefs[s]
        : {};
    const outS = {};

    for (const k of Object.keys(defS)) {
      outS[k] = { ...defS[k], ...coerceSeriesPref(gotS[k]) };
    }
    for (const k of Object.keys(gotS)) {
      if (!outS[k]) outS[k] = coerceSeriesPref(gotS[k]);
    }

    base.seriesPrefs[s] = outS;
  }

  base.bandFillAlphaLight = definedNum(base.bandFillAlphaLight)
    ? Math.max(0, Math.min(1, Number(base.bandFillAlphaLight)))
    : DEFAULT_FD_PREFS.bandFillAlphaLight;

  base.bandFillAlphaDark = definedNum(base.bandFillAlphaDark)
    ? Math.max(0, Math.min(1, Number(base.bandFillAlphaDark)))
    : DEFAULT_FD_PREFS.bandFillAlphaDark;

  return base;
}

function mergeChartsPrefAll(raw) {
  const out = raw && typeof raw === "object" ? deepClone(raw) : {};
  const v = Number(out.v || 1);
  out.v = Math.max(v, FD_PREFS_VERSION);
  out.forecastDash = mergeForecastDashPrefs(out.forecastDash);
  return out;
}
// ---------- Safe access helpers (avoid no-undef) ----------

function getForecastDashPrefsSafe(all) {
  if (!all || typeof all !== "object") {
    return mergeForecastDashPrefs(DEFAULT_FD_PREFS);
  }
  return mergeForecastDashPrefs(all.forecastDash);
}

function pickFdSeriesPref(all, stream, kind) {
  const fd = getForecastDashPrefsSafe(all);
  return (
    fd?.seriesPrefs?.[stream]?.[kind] ||
    DEFAULT_FD_PREFS.seriesPrefs?.[stream]?.[kind] ||
    {}
  );
}
function chartsPrefCacheKey({ companySubdomain, userId }) {
  const c = String(companySubdomain || "default")
    .trim()
    .toLowerCase();
  const u = String(userId || "anon")
    .trim()
    .toLowerCase();
  return `og_charts_pref:${c}:${u}`;
}

function readChartsPrefCache(key) {
  try {
    const s = localStorage.getItem(key);
    if (!s) return null;
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function writeChartsPrefCache(key, obj) {
  try {
    localStorage.setItem(key, JSON.stringify(obj));
  } catch {}
}

function authHeadersFromPassThrough(passThrough) {
  const token =
    passThrough?.token ||
    passThrough?.jwt ||
    passThrough?.access_token ||
    passThrough?.Authorization ||
    "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchChartsPrefFromUserDetails({
  api,
  userId,
  passThrough,
  signal,
}) {
  const url = `${api}?id=${encodeURIComponent(userId)}&chartsPref=1`;
  const res = await fetch(url, {
    signal,
    headers: {
      Accept: "application/json",
      ...authHeadersFromPassThrough(passThrough),
    },
  });
  const txt = await res.text().catch(() => "");
  if (!res.ok)
    throw new Error(`UserDetails ${res.status}: ${txt || res.statusText}`);
  const json = txt ? JSON.parse(txt) : {};
  return json?.ChartsPref ?? null;
}

async function patchChartsPrefToUserDetails({
  api,
  userId,
  passThrough,
  chartsPrefAll,
}) {
  const res = await fetch(api, {
    method: "PATCH",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...authHeadersFromPassThrough(passThrough),
    },
    body: JSON.stringify({ UserID: userId, ChartsPref: chartsPrefAll }),
  });
  const txt = await res.text().catch(() => "");
  if (!res.ok)
    throw new Error(
      `UserDetails PATCH ${res.status}: ${txt || res.statusText}`,
    );
  return txt ? JSON.parse(txt) : {};
}

function applySeriesPrefToEchartsSeries(series, pref, { defaultColor }) {
  const p = coerceSeriesPref(pref);
  const color =
    p.color || defaultColor || series?.itemStyle?.color || undefined;

  if (p.render === "line" || p.render === "bar" || p.render === "scatter") {
    series.type = p.render;
  }

  series.itemStyle = { ...(series.itemStyle || {}), color };

  if (series.type === "line") {
    series.showSymbol = series.showSymbol ?? false;
    series.lineStyle = { ...(series.lineStyle || {}) };
    if (p.width != null) series.lineStyle.width = Number(p.width);
    if (p.dash) series.lineStyle.type = p.dash;
    if (p.opacity != null)
      series.lineStyle.opacity = Math.max(0, Math.min(1, Number(p.opacity)));
    if (typeof p.smooth === "boolean") series.smooth = p.smooth;
  }

  if (series.type === "bar") {
    if (p.barWidth != null) series.barWidth = Number(p.barWidth);
    if (p.opacity != null)
      series.itemStyle.opacity = Math.max(0, Math.min(1, Number(p.opacity)));
  }

  if (series.type === "scatter") {
    if (p.symbol) series.symbol = p.symbol;
    if (p.symbolSize != null) series.symbolSize = Number(p.symbolSize);
    if (p.opacity != null)
      series.itemStyle.opacity = Math.max(0, Math.min(1, Number(p.opacity)));
  }

  return series;
}

/* --------------------------------------------------------------------
 * USER FORECAST (editable P50 override)
 * - Stored client-side (localStorage) keyed by company + lease/tags scope
 * - Overrides ONLY the mid (p50) forecast stream for charts + econ
 * - Does NOT modify / replace the base DCA model output (data.forecast)
 * -------------------------------------------------------------------- */

function userForecastStorageKey({
  companySubdomain,
  lease,
  tagsMode,
  selectedTags,
}) {
  const scope = selectedTags?.length
    ? `tags:${tagsMode}:${selectedTags.join(",")}`
    : `lease:${lease || ""}`;
  return `og_forecast_user_p50:${companySubdomain || "default"}:${scope}`;
}

function normalizeUserForecastShape(obj) {
  const out = { Oil: [], Gas: [], Water: [] };
  if (!obj || typeof obj !== "object") return out;
  for (const s of STREAMS) {
    const arr = obj?.[s];
    if (!Array.isArray(arr)) continue;
    out[s] = arr
      .filter((p) => p && p.date)
      .map((p) => ({
        date: String(p.date).slice(0, 10),
        q: definedNum(p.q) ? Number(p.q) : null,
      }))
      .filter((p) => definedNum(p.q))
      .sort((a, b) => +parseISO(a.date) - +parseISO(b.date));
  }
  return out;
}

// Accepts either:
// A) 2-column "date,Oil,Gas,Water"
// B) existing export shape from toCSV() (looks for "Oil Forecast(p50)" etc.)
function parseUserForecastCSV(csvText) {
  const txt = String(csvText || "").trim();
  if (!txt) return { Oil: [], Gas: [], Water: [] };

  const lines = txt.split(/\r?\n/).filter((l) => l.trim().length);
  if (lines.length < 2) return { Oil: [], Gas: [], Water: [] };

  const splitCSV = (line) => {
    // minimal CSV splitter (handles quotes)
    const out = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQ && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQ = !inQ;
        }
      } else if (ch === "," && !inQ) {
        out.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };

  const header = splitCSV(lines[0]).map((h) => h.replace(/^"|"$/g, ""));
  const idxDate = header.findIndex((h) => h.toLowerCase() === "date");

  const pickCol = (stream) => {
    const sLow = stream.toLowerCase();
    const direct = header.findIndex((h) => h.toLowerCase() === sLow);
    if (direct >= 0) return direct;

    // toCSV() exports: "Oil Forecast(p50)" etc.
    const p50 = header.findIndex(
      (h) => h.toLowerCase() === `${sLow} forecast(p50)`,
    );
    if (p50 >= 0) return p50;

    // tolerate variants
    const anyP50 = header.findIndex(
      (h) => h.toLowerCase().includes(sLow) && h.toLowerCase().includes("p50"),
    );
    return anyP50;
  };

  const idxOil = pickCol("Oil");
  const idxGas = pickCol("Gas");
  const idxWat = pickCol("Water");

  const out = { Oil: [], Gas: [], Water: [] };
  for (let i = 1; i < lines.length; i++) {
    const cols = splitCSV(lines[i]);
    const d = (cols[idxDate] || "").replace(/^"|"$/g, "").slice(0, 10);
    if (!d || d.length !== 10) continue;

    const read = (idx) => {
      if (idx < 0) return null;
      const raw = (cols[idx] || "").replace(/^"|"$/g, "");
      const v = Number(raw);
      return Number.isFinite(v) ? v : null;
    };

    const o = read(idxOil);
    const g = read(idxGas);
    const w = read(idxWat);

    if (definedNum(o)) out.Oil.push({ date: d, q: o });
    if (definedNum(g)) out.Gas.push({ date: d, q: g });
    if (definedNum(w)) out.Water.push({ date: d, q: w });
  }

  return normalizeUserForecastShape(out);
}

function userForecastToCSV(userForecast, { datesHint } = {}) {
  const uf = normalizeUserForecastShape(userForecast);
  const all = new Set();
  for (const s of STREAMS) (uf[s] || []).forEach((p) => all.add(p.date));
  (datesHint || []).forEach((d) => all.add(String(d).slice(0, 10)));

  const dates = [...all].sort((a, b) => +parseISO(a) - +parseISO(b));
  const header = ["date", "Oil", "Gas", "Water"];
  const rows = [header.join(",")];

  const m = {};
  for (const s of STREAMS) {
    m[s] = new Map((uf[s] || []).map((p) => [p.date, p.q]));
  }

  for (const d of dates) {
    rows.push(
      [d, m.Oil.get(d) ?? "", m.Gas.get(d) ?? "", m.Water.get(d) ?? ""]
        .map((x) => (typeof x === "string" ? csvEscape(x) : x))
        .join(","),
    );
  }

  return rows.join("\n");
}

function pairsToUserForecast(points) {
  // points: [[date,value], ...]
  return (points || [])
    .filter((p) => Array.isArray(p) && p[0] && definedNum(p[1]))
    .map(([d, v]) => ({ date: String(d).slice(0, 10), q: Number(v) }))
    .sort((a, b) => +parseISO(a.date) - +parseISO(b.date));
}

function userForecastToOverrides(userForecast) {
  const uf = normalizeUserForecastShape(userForecast);
  const overrides = { Oil: {}, Gas: {}, Water: {} };
  for (const s of STREAMS) {
    for (const p of uf[s] || []) {
      if (p?.date && definedNum(p?.q)) overrides[s][p.date] = Number(p.q);
    }
  }
  return overrides;
}

function parseUserForecastOverridesCSV(csvText) {
  const uf = parseUserForecastCSV(csvText);
  return userForecastToOverrides(uf);
}

// ---------- API (collapsed from api.ts) ----------
// Always hit the canonical staged-overlay endpoint. Auth/context (token,
// grant, Company, UserID, Name, App) flows from the URL — same pattern the
// rest of the app uses (see src/services/ogpUrlContext.js + lcsClient.js).
function buildOverlayUrl(params) {
  const base = resolveApiBase();
  const url = new URL("/api/overlay_staged.php", base);

  const p = typeof params === "string" ? { lease: params } : params || {};

  // include the params your endpoint actually supports + pass-through keys if present
  const allowed = [
    "lease",
    "run_id",
    "horizon",
    "streams",
    "from",
    "thru",
    "tags",
    "tags_mode",
    "aggregate",
    "include_leases",
    "include_inactive",
    "limit",
    "token",
    "grant",
    "UserID",
    "Company",
    "Name",
    "App",
    "include_llm",
    "econ_context",
    "notes",
  ];

  for (const k of allowed) {
    const v = p?.[k];
    if (v === undefined || v === null || v === "") continue;
    url.searchParams.set(k, String(v));
  }

  // Local-dev convenience: if the page didn't carry token/grant on its URL
  // (i.e. you opened http://localhost:3000/decision without the OgPumper
  // bootstrap params), fall back to the gitignored .env.development.local
  // values. In a deployed build these env vars are unset, so this code path
  // never injects anything — auth comes from the page URL.
  if (typeof process !== "undefined" && process?.env) {
    if (!url.searchParams.has("token") && process.env.REACT_APP_LCS_TOKEN) {
      url.searchParams.set("token", process.env.REACT_APP_LCS_TOKEN);
    }
    if (!url.searchParams.has("grant") && process.env.REACT_APP_LCS_GRANT) {
      url.searchParams.set("grant", process.env.REACT_APP_LCS_GRANT);
    }
  }

  return url.toString();
}

async function fetchOverlay(params, signal) {
  const url = buildOverlayUrl(params);

  const res = await fetch(url, {
    signal,
    headers: { Accept: "application/json" },
  });

  // Friendlier error body (JSON or text)
  if (!res.ok) {
    let body = "";
    try {
      const ct = res.headers.get("content-type") || "";
      body = ct.includes("application/json")
        ? JSON.stringify(await res.json())
        : await res.text();
    } catch {}
    throw new Error(`Overlay API ${res.status}: ${body || res.statusText}`);
  }

  const jsonRaw = await res.json();

  // Normalize payload (also tolerate tags legacy multi-mode if server returns it)
  let json = jsonRaw || {};
  if (json?.mode === "multi" && Array.isArray(json?.leases)) {
    // If someone hit aggregate=0, salvage first OK lease payload so chart doesn't crash.
    const firstOk =
      json.leases.find((x) => x && x.ok) || json.leases[0] || null;
    if (firstOk && (firstOk.actual || firstOk.forecast)) {
      json = {
        ...firstOk,
        mode: "multi",
        meta: {
          ...(json.meta || {}),
          _note:
            "Server returned multi-lease payload; client displayed first lease. Use aggregate=1 for tag aggregation.",
          _multi_count: json.leases.length,
          tags: json.tags || json.meta?.tags || undefined,
        },
      };
    }
  }

  // Normalize
  json.actual = json.actual || {};
  json.forecast = json.forecast || {};
  json.anomalies = json.anomalies || {};
  json.metrics = json.metrics || {};
  json.meta = json.meta || {};
  return json;
}

/* --------------------------------------------------------------------
 * ECON LAYER (expanded)
 * - Computes daily cashflow from actual + forecast(p50)
 * - Includes:
 *   prices, diffs, shrink, marketing/transport, WI/NRI, taxes,
 *   LOE (fixed/var/water/chem/electric/overhead), capex timing, abandonment,
 *   downtime factor, discounting (PV / PV10), decision rules
 * - Decision point metrics:
 *   first negative cashflow date, economic limit (rolling avg or consecutive),
 *   payout date, PV(Discount), breakeven oil for PV=0, breakeven oil for min CF>=0,
 *   max fixed LOE for PV=0 (sensitivity), scenario compare (Base/Bear/Bull)
 * -------------------------------------------------------------------- */
function boeFrom(oilBbl, gasMcf, nglBbl = 0) {
  const oil = definedNum(oilBbl) ? oilBbl : 0;
  const gas = definedNum(gasMcf) ? gasMcf : 0;
  const ngl = definedNum(nglBbl) ? nglBbl : 0;
  return oil + ngl + gas / 6.0;
}

function seriesMapActual(actualArr) {
  const m = new Map();
  (actualArr || []).forEach((p) => {
    if (p?.date) m.set(p.date, definedNum(p.q) ? Number(p.q) : null);
  });
  return m;
}

function seriesMapForecastMid(forecastEntry) {
  const { mid } = extractForecast(forecastEntry);
  const m = new Map();
  (mid || []).forEach(([d, v]) => {
    m.set(d, definedNum(v) ? Number(v) : null);
  });
  return m;
}

function computeEffectiveWI(econ) {
  const royalty = clamp01(econ.royalty);
  const wiBase = clamp01(econ.workingInterest);
  const nriOverride = Number(econ.nriOverride || 0);
  if (Number.isFinite(nriOverride) && nriOverride > 0) {
    // Treat as NRI; derive WI ~= NRI/(1-royalty)
    const denom = Math.max(1e-9, 1 - royalty);
    return clamp01(nriOverride / denom);
  }
  return wiBase;
}

function discountFactor(annualRate, daysFromStart) {
  const r = Number(annualRate);
  const t = Number(daysFromStart);
  if (!Number.isFinite(r) || r <= 0) return 1;
  if (!Number.isFinite(t) || t <= 0) return 1;
  return 1 / Math.pow(1 + r, t / 365.0);
}

function econRow({ date, oil, gas, water, econ, capex = 0, startDate }) {
  const uptime = 1 - clamp01(econ.downtimePct);

  const oilUnit = definedNum(econ.oilUnitFactor) ? econ.oilUnitFactor : 1;
  const gasUnit = definedNum(econ.gasUnitFactor) ? econ.gasUnitFactor : 1;
  const watUnit = definedNum(econ.waterUnitFactor) ? econ.waterUnitFactor : 1;

  const oilBbl = (definedNum(oil) ? Number(oil) : 0) * oilUnit * uptime;
  const gasMcf = (definedNum(gas) ? Number(gas) : 0) * gasUnit * uptime;
  const watBbl = (definedNum(water) ? Number(water) : 0) * watUnit * uptime;

  const oilPrice = Number(econ.oilPrice) || 0;
  const gasPrice = Number(econ.gasPrice) || 0;

  const netOilPrice = Math.max(
    0,
    oilPrice -
      (Number(econ.oilDiff) || 0) -
      (Number(econ.oilMarketingPerBbl) || 0) -
      (Number(econ.oilTransportPerBbl) || 0),
  );
  const netGasPrice = Math.max(
    0,
    gasPrice -
      (Number(econ.gasDiff) || 0) -
      (Number(econ.gasMarketingPerMcf) || 0) -
      (Number(econ.gasTransportPerMcf) || 0),
  );

  // NGL (optional)
  const nglYield = Math.max(0, Number(econ.nglYieldBblPerMcf) || 0);
  const nglMode = String(econ.nglMode || "pct_wti").toLowerCase();
  const nglPct = clamp01(econ.nglPctOfWti);
  const nglPerGal = Math.max(0, Number(econ.nglPricePerGal) || 0);
  const nglPricePerBbl =
    nglMode === "per_gal" ? nglPerGal * 42 : oilPrice * nglPct;
  const nglBbl = gasMcf * nglYield; // derived from produced gas
  const grossNgl = nglBbl * Math.max(0, nglPricePerBbl);

  const salesGasMcf = gasMcf * (1 - clamp01(econ.gasShrink));
  const grossOil = oilBbl * netOilPrice;
  const grossGas = salesGasMcf * netGasPrice;

  const grossRev = grossOil + grossGas + grossNgl;

  const royaltyRate = clamp01(econ.royalty);
  const wi = computeEffectiveWI(econ);

  const royalty = grossRev * royaltyRate;

  // Taxes (simple): apply oil sev tax to NGL (liquids)
  const oilSev = clamp01(econ.oilSevTax);
  const gasSev = clamp01(econ.gasSevTax);
  const sevTax = grossOil * oilSev + grossGas * gasSev + grossNgl * oilSev;

  const adVal = grossRev * clamp01(econ.adValoremTax);

  const revAfterRoyaltyTaxes = grossRev - royalty - sevTax - adVal;

  // Owner revenue on WI share
  const ownerRev = revAfterRoyaltyTaxes * wi;

  const boe = boeFrom(oilBbl, gasMcf, nglBbl);

  // Opex (assume WI share of costs)
  const fixedLoeDaily = (Number(econ.fixedLoePerMonth) || 0) / 30.0;
  const baseMaintDaily = (Number(econ.baseMaintPerMonth) || 0) / 30.0;
  const overheadDaily = (Number(econ.overheadPerMonth) || 0) / 30.0;

  const varLoe = boe * (Number(econ.varLoePerBoe) || 0);
  const waterDisp = watBbl * (Number(econ.waterDisposalPerBbl) || 0);

  // New direct variable stream-based costs
  const varOil = oilBbl * (Number(econ.varOilPerBbl) || 0);
  const varGas = gasMcf * (Number(econ.varGasPerMcf) || 0);

  // Legacy buckets (optional)
  const chemicals = boe * (Number(econ.chemicalsPerBoe) || 0);
  const electricity = boe * (Number(econ.electricityPerBoe) || 0);
  const lowRateExtraDaily =
    boe < (Number(econ.lowRateBoePerDayThreshold) || 0)
      ? (Number(econ.lowRateExtraMaintPerMonth) || 0) / 30.0
      : 0;

  const opexRaw =
    fixedLoeDaily +
    varLoe +
    waterDisp +
    varOil +
    varGas +
    baseMaintDaily +
    lowRateExtraDaily +
    chemicals +
    electricity +
    overheadDaily;

  const opex = opexRaw * wi;

  const capexOwner = (Number(capex) || 0) * wi;

  const cashflow = ownerRev - opex - capexOwner;

  const tDays = startDate ? (daysBetweenISO(startDate, date) ?? 0) : 0;
  const pv =
    cashflow * discountFactor(Number(econ.discountRateAnnual) || 0, tDays);

  return {
    date,
    oil: oilBbl,
    gas: gasMcf,
    water: watBbl,
    ngl: nglBbl,
    boe,
    netOilPrice,
    netGasPrice,
    nglPricePerBbl,
    grossOil,
    grossGas,
    grossNgl,
    grossRev,
    royalty,
    sevTax,
    adVal,
    wi,
    ownerRev,
    opex,
    capex: capexOwner,
    cashflow,
    pv,
  };
}

function firstNegative(rows) {
  const hit = (rows || []).find(
    (r) => definedNum(r?.cashflow) && r.cashflow < 0,
  );
  return hit ? hit.date : "";
}

function rollingAvgNegativeDate(rows, windowDays = 14) {
  const w = Math.max(2, Math.min(90, parseInt(windowDays || "14", 10)));
  const arr = (rows || []).filter((r) => r && r.date).slice();
  if (arr.length < w) return "";
  for (let i = w - 1; i < arr.length; i++) {
    let sum = 0;
    let ok = 0;
    for (let j = i - (w - 1); j <= i; j++) {
      const v = arr[j]?.cashflow;
      if (definedNum(v)) {
        sum += v;
        ok += 1;
      }
    }
    if (ok > 0) {
      const avg = sum / ok;
      if (avg < 0) return arr[i].date;
    }
  }
  return "";
}

function consecutiveNegativeDate(rows, consecutiveDays = 14) {
  const need = Math.max(2, Math.min(90, parseInt(consecutiveDays || "14", 10)));
  let run = 0;
  for (const r of rows || []) {
    if (definedNum(r?.cashflow) && r.cashflow < 0) run += 1;
    else run = 0;
    if (run >= need) return r.date;
  }
  return "";
}

function payoutDate(rows) {
  // First date cumulative cashflow >= 0 (useful when you include upfront capex)
  let cum = 0;
  for (const r of rows || []) {
    const cf = definedNum(r?.cashflow) ? r.cashflow : 0;
    cum += cf;
    if (cum >= 0) return r.date;
  }
  return "";
}

function sumOf(rows, key) {
  return (rows || []).reduce(
    (s, r) => s + (definedNum(r?.[key]) ? r[key] : 0),
    0,
  );
}

function applyScenario(econ, scenario) {
  const sc = scenario || { oilMult: 1, gasMult: 1, opexMult: 1, name: "Base" };
  const o = { ...econ };

  const oilMult = Number(sc.oilMult) || 1;
  const gasMult = Number(sc.gasMult) || 1;
  const opexMult = Number(sc.opexMult) || 1;

  o.oilPrice = (Number(o.oilPrice) || 0) * oilMult;
  o.gasPrice = (Number(o.gasPrice) || 0) * gasMult;

  // Opex scale (optional but useful for sensitivity)
  o.fixedLoePerMonth = (Number(o.fixedLoePerMonth) || 0) * opexMult;
  o.varLoePerBoe = (Number(o.varLoePerBoe) || 0) * opexMult;
  o.waterDisposalPerBbl = (Number(o.waterDisposalPerBbl) || 0) * opexMult;
  o.baseMaintPerMonth = (Number(o.baseMaintPerMonth) || 0) * opexMult;
  o.lowRateExtraMaintPerMonth =
    (Number(o.lowRateExtraMaintPerMonth) || 0) * opexMult;
  o.chemicalsPerBoe = (Number(o.chemicalsPerBoe) || 0) * opexMult;
  o.electricityPerBoe = (Number(econ.electricityPerBoe) || 0) * opexMult;
  o.overheadPerMonth = (Number(o.overheadPerMonth) || 0) * opexMult;

  return o;
}

function solveBisection({ lo, hi, fn, target = 0, maxIter = 40, eps = 1e-6 }) {
  let a = lo;
  let b = hi;
  let fa = fn(a) - target;
  let fb = fn(b) - target;

  if (!Number.isFinite(fa) || !Number.isFinite(fb)) return null;
  if (Math.abs(fa) < eps) return a;
  if (Math.abs(fb) < eps) return b;

  // If not bracketed, no solution in [lo, hi]
  if (fa * fb > 0) return null;

  for (let i = 0; i < maxIter; i++) {
    const m = (a + b) / 2;
    const fm = fn(m) - target;
    if (!Number.isFinite(fm)) return null;
    if (Math.abs(fm) < eps) return m;
    if (fa * fm <= 0) {
      b = m;
      fb = fm;
    } else {
      a = m;
      fa = fm;
    }
  }
  return (a + b) / 2;
}

function toEconCSV(econResult) {
  const rows = econResult?.rows || [];
  const header = [
    "date",
    "oil",
    "gas",
    "water",
    "ngl",
    "boe",
    "grossRev",
    "grossNgl",
    "royalty",
    "sevTax",
    "adVal",
    "wi",
    "ownerRev",
    "opex",
    "capex",
    "cashflow",
    "cumCashflow",
    "pv",
  ];

  const out = [header.join(",")];

  let cum = 0;
  for (const r of rows) {
    const cf = definedNum(r.cashflow) ? r.cashflow : 0;
    cum += cf;

    const line = [
      r.date,
      r.oil,
      r.gas,
      r.water,
      r.ngl,
      r.boe,
      r.grossRev,
      r.grossNgl,
      r.royalty,
      r.sevTax,
      r.adVal,
      r.wi,
      r.ownerRev,
      r.opex,
      r.capex,
      r.cashflow,
      cum,
      r.pv,
    ].map((x) =>
      typeof x === "string" ? csvEscape(x) : definedNum(x) ? String(x) : "",
    );
    out.push(line.join(","));
  }

  return out.join("\n");
}

function Modal({ open, title, onClose, children, footer }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="modalOverlay" role="dialog" aria-modal="true">
      <div className="modalCard">
        <div className="modalHeader">
          <div>
            <div className="modalTitle">{title}</div>
            <div className="modalSub">
              Decision metrics, breakevens, PV, and assumptions
            </div>
          </div>
          <button className="btn" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="modalBody">{children}</div>
        {footer ? <div className="modalFooter">{footer}</div> : null}
      </div>
    </div>
  );
}
function normalizeTypes(a) {
  const t = a?.types;
  if (Array.isArray(t)) return t.map((x) => String(x).toLowerCase());
  if (typeof t === "string" && t.trim()) {
    return t
      .split(/[,\s]+/)
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean);
  }
  return [];
}

function shouldPlotAnomaly(a) {
  const types = normalizeTypes(a);
  // Show only operationally meaningful anomalies
  if (types.includes("spike")) return true;
  // Optional: allow big jumps (guarded by jump magnitude)
  if (types.includes("jump") && Number(a?.jump) >= 2) return true;
  return false;
}

// ---------- Component ----------
export default function ForecastDashboard() {
  // Parse initial query once (prevents conditional hooks)
  const init = useMemo(() => {
    const qs = new URLSearchParams(window.location.search);

    const userIdRaw = String(qs.get("UserID") || qs.get("user") || "")
      .trim()
      .toLowerCase();
    const isAdmin = ["admin", "kristie"].includes(
      String(userIdRaw || "")
        .trim()
        .toLowerCase(),
    );

    // Keep pass-through params intact (token/grant/etc.)
    const passThrough = {};
    ["Company", "Name", "UserID", "token", "grant", "App"].forEach((k) => {
      const v = qs.get(k);
      if (v !== null && v !== undefined && v !== "") passThrough[k] = v;
    });

    const companyLabelRaw =
      qs.get("Name") || qs.get("Company") || "Production Forecasts";
    let companyLabel = companyLabelRaw;
    try {
      companyLabel = decodeURIComponent(companyLabelRaw);
    } catch {}

    // Use Company subdomain if provided, otherwise default to the SAME host as the app base.
    const baseHost = (() => {
      try {
        return new URL(resolveApiBase()).origin;
      } catch {
        return "https://assent.ogpumper.net";
      }
    })();
    const companySubdomain = (qs.get("Company") || "").trim().toLowerCase();
    const companyBase = companySubdomain
      ? `https://${companySubdomain}.ogpumper.net`
      : baseHost;

    const LEASES_API = (() => {
      const override = window.__LEASES_API__;
      if (override && String(override).trim()) return String(override).trim();
      return `${companyBase}/api/leases.php?onlyactivelease=1`;
    })();

    const TAGS_API = (() => {
      const override = window.__TAGS_API__;
      if (override && String(override).trim()) return String(override).trim();
      return `${companyBase}/api/usertags.php`;
    })();

    const USERDETAILS_API = (() => {
      const override = window.__USERDETAILS_API__;
      if (override && String(override).trim()) return String(override).trim();
      return `${companyBase}/api/users.php`;
    })();
    // Optional date params (kept for OGCharts compatibility / deep links)
    const from = String(qs.get("from") || qs.get("From") || "").trim();
    const thru = String(qs.get("thru") || qs.get("Thru") || "").trim();

    // IMPORTANT: NEVER honor "~ALL~" from URL as a selected lease.
    // We treat it as "not provided" so the UI picks the first real lease after options load.
    const leaseRaw = String(qs.get("lease") ?? qs.get("LeaseID") ?? "").trim();
    const leaseSan =
      leaseRaw && leaseRaw.toLowerCase() === "~all~" ? "" : leaseRaw;
    const leaseProvided = Boolean(leaseSan) && leaseSan !== "~ALL~";
    const lease = leaseProvided ? leaseSan : "";

    const horizon = parseInt(qs.get("horizon") || "60", 10);
    const streams = qs.get("streams")?.split(",").filter(Boolean) || [
      "Oil",
      "Gas",
      "Water",
    ];
    const logScale = (qs.get("scale") ?? "log") === "log";
    const dark = qs.get("theme") === "dark";

    const showMA = qs.get("showMA") ? qs.get("showMA") === "true" : true;
    const maWindow = qs.get("maWindow")
      ? Math.max(2, Math.min(60, parseInt(qs.get("maWindow") || "7", 10)))
      : 7;
    const showOutliers = qs.get("outliers")
      ? qs.get("outliers") === "true"
      : false;

    // P10/P90 bands (default OFF — only show when explicitly enabled)
    const showBands = qs.get("bands") ? qs.get("bands") === "true" : false;

    // User editable P50 (default OFF)
    const useUserForecast = qs.get("userForecast")
      ? qs.get("userForecast") === "true"
      : false;

    const zeroAsNull = qs.get("zeroAsNull")
      ? qs.get("zeroAsNull") === "true"
      : true;
    const cumulativeMode = qs.get("cumulative") ?? "off"; // Forecast display mode
    // - base: use server DCA p50 (and p10/p90 if present)
    // - user: use user-edited p50 override (client-side) when available
    const forecastModeRaw = String(qs.get("fmode") || "base")
      .trim()
      .toLowerCase();
    const forecastMode = forecastModeRaw === "user" ? "user" : "base";

    // TAGS: TagID-only (no TagDesc reconciliation)
    const selectedTags = normalizeTagsParam(qs.get("tags") ?? "");
    const tagsModeRaw = String(qs.get("tags_mode") || "any")
      .trim()
      .toLowerCase();
    const tagsMode = tagsModeRaw === "all" ? "all" : "any";
    const includeInactive = String(qs.get("include_inactive") || "").trim();
    const includeInactiveBool =
      includeInactive === "1" || includeInactive.toLowerCase() === "true";

    return {
      isAdmin,
      userIdRaw,
      passThrough,
      companyLabel,
      companySubdomain,
      LEASES_API,
      TAGS_API,
      USERDETAILS_API,
      from,
      thru,
      lease,
      leaseProvided,
      horizon,
      streams,
      logScale,
      dark,
      showMA,
      maWindow,
      showOutliers,
      showBands,
      useUserForecast,
      zeroAsNull,
      cumulativeMode,
      selectedTags,
      tagsMode,
      includeInactive: includeInactiveBool,
    };
  }, []);

  // Read params with OGCharts fallbacks
  const [lease, setLease] = useState(init.lease);
  const [horizon, setHorizon] = useState(init.horizon);
  const [streams, setStreams] = useState(init.streams);

  const [logScale, setLogScale] = useState(init.logScale);
  const [dark, setDark] = useState(init.dark);

  const [showMA, setShowMA] = useState(init.showMA);
  const [maWindow, setMaWindow] = useState(init.maWindow);
  const [showOutliers, setShowOutliers] = useState(init.showOutliers);
  const [showBands, setShowBands] = useState(init.showBands);

  // Chart prefs (server-stored in users.ChartsPref)
  const [chartsPrefAll, setChartsPrefAll] = useState(null);
  const [chartsPrefLoading, setChartsPrefLoading] = useState(false);
  const [chartsPrefSaving, setChartsPrefSaving] = useState(false);
  const [chartsPrefError, setChartsPrefError] = useState(null);

  const [chartPrefsOpen, setChartPrefsOpen] = useState(false);
  const [chartPrefsDraft, setChartPrefsDraft] = useState(null);
  const [chartPrefsTab, setChartPrefsTab] = useState("Oil");
  const [chartPrefsPickerKey, setChartPrefsPickerKey] = useState(null);

  // User editable P50...
  // User editable P50 (applied as a layer on top of the base DCA P50)
  const [useUserForecast, setUseUserForecast] = useState(init.useUserForecast);
  const [userForecastOpen, setUserForecastOpen] = useState(false);
  const [userForecastCfg, setUserForecastCfg] = useState(null);
  const [userForecastDraft, setUserForecastDraft] = useState(null);

  const [zeroAsNull, setZeroAsNull] = useState(init.zeroAsNull);
  const [cumulativeMode, setCumulativeMode] = useState(init.cumulativeMode);

  // Forecast display toggle:
  // - base: use server forecast (DCA output)
  // - user: override ONLY p50 with user-edited values (stored client-side)
  // Single source of truth for forecast mode (avoid drift)
  const forecastMode = useUserForecast ? "user" : "base";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [latencyMs, setLatencyMs] = useState(null);
  const [runAt, setRunAt] = useState(null);

  // Leases/tags options
  const [leaseOptions, setLeaseOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [ltLoading, setLtLoading] = useState(false);
  const [ltError, setLtError] = useState(null);

  const [selectedTags, setSelectedTags] = useState(init.selectedTags);
  const [tagsMode, setTagsMode] = useState(init.tagsMode);
  const [includeInactive, setIncludeInactive] = useState(init.includeInactive);

  // ECON assumptions (expanded defaults)
  const [econ, setEcon] = useState({
    // Prices & diffs
    oilPrice: 70,
    gasPrice: 3.0,
    oilDiff: 3.0,
    gasDiff: 0.3,
    gasShrink: 0.05,
    // NGL (optional)
    nglMode: "pct_wti", // "pct_wti" | "per_gal"
    nglPctOfWti: 0.45, // 0..1 (only if pct_wti)
    nglPricePerGal: 0.0, // $/gal (only if per_gal)
    nglYieldBblPerMcf: 0.0, // bbl NGL per mcf gas

    // Midstream / marketing / transport
    oilMarketingPerBbl: 0.0,
    gasMarketingPerMcf: 0.0,
    oilTransportPerBbl: 0.0,
    gasTransportPerMcf: 0.0,

    // Ownership & taxes
    royalty: 0.2,
    workingInterest: 1.0,
    nriOverride: 0.0, // optional: if >0, treated as NRI (derives WI)
    oilSevTax: 0.046,
    gasSevTax: 0.075,
    adValoremTax: 0.0,

    // Opex (LOE)
    // Opex (LOE)
    fixedLoePerMonth: 4500,
    varLoePerBoe: 2.25,
    waterDisposalPerBbl: 1.25,

    // New direct variable cost controls
    varOilPerBbl: 0.0, // $ per bbl oil
    varGasPerMcf: 0.0, // $ per mcf gas

    // Optional legacy buckets (can leave at 0)
    chemicalsPerBoe: 0.0,
    electricityPerBoe: 0.0,
    baseMaintPerMonth: 0.0,

    overheadPerMonth: 0.0,
    lowRateBoePerDayThreshold: 8,
    lowRateExtraMaintPerMonth: 900,
    // Capex timing
    capexUpfront: 0,
    capexDate: "", // default = econ window start
    abandonmentCost: 0,
    abandonmentDate: "", // default = last day in window

    // Uptime factor
    downtimePct: 0.0,

    // Discounting
    discountRateAnnual: 0.1,

    // Units scaling (if needed)
    oilUnitFactor: 1.0,
    gasUnitFactor: 1.0,
    waterUnitFactor: 1.0,

    // Decision rules / window
    unprofitableSoonDays: 60,
    econLimitRollingDays: 14,
    econLimitConsecutiveNegDays: 14,
  });

  const ECON_SCENARIOS = useMemo(
    () => [
      { key: "Base", name: "Base", oilMult: 1.0, gasMult: 1.0, opexMult: 1.0 },
      {
        key: "Bear",
        name: "Bear",
        oilMult: 0.85,
        gasMult: 0.8,
        opexMult: 1.05,
      },
      {
        key: "Bull",
        name: "Bull",
        oilMult: 1.15,
        gasMult: 1.2,
        opexMult: 0.98,
      },
    ],
    [],
  );
  const [econScenarioKey, setEconScenarioKey] = useState("Base");
  const [econModalOpen, setEconModalOpen] = useState(false);
  const [econTab, setEconTab] = useState("summary"); // summary | assumptions | table
  const [showAdvancedOpex, setShowAdvancedOpex] = useState(false);

  const chartRef = useRef(null);
  const ufFileRef = useRef(null);
  const abortRef = useRef(null);
  const didInitialLoadRef = useRef(false);

  const unitsByStream = data?.meta?.units || {};
  const tagsActive = selectedTags.length > 0;
  const actualDomain = useMemo(() => {
    if (!data?.actual) return { from: "", thru: "" };
    return actualDomainFromActuals(data.actual);
  }, [data]);

  const TAG_DESELECT_TIP =
    `To deselect tags:\n` +
    `• Windows/Linux: hold Ctrl and click selected tags\n` +
    `• Mac: hold ⌘ (Command) and click selected tags\n` +
    `Tip: click-drag works for ranges too.`;

  // -----------------------------
  // User editable P50 implementation
  // - Stored per (company + lease) or (company + tags) in localStorage
  // - Applied as a LIGHT layer on top of base DCA P50 (so we never "drop DCA")
  //   1) per-stream scale multiplier (default 1.00)
  //   2) optional per-date absolute overrides (advanced)
  // -----------------------------

  const userForecastKey = useMemo(() => {
    const base = String(init.companySubdomain || "default").toLowerCase();
    if (tagsActive) {
      const tagKey = (selectedTags || [])
        .slice()
        .map((s) => String(s))
        .sort()
        .join(",");
      return `uf_${base}_tags_${tagKey || "all"}`;
    }
    return `uf_${base}_lease_${String(lease || "")}`;
  }, [init.companySubdomain, tagsActive, selectedTags, lease]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(userForecastKey);
      if (!raw) {
        setUserForecastCfg(null);
        return;
      }
      const parsed = JSON.parse(raw);
      setUserForecastCfg(parsed && typeof parsed === "object" ? parsed : null);
    } catch {
      setUserForecastCfg(null);
    }
  }, [userForecastKey]);

  function clearUserForecast() {
    try {
      localStorage.removeItem(userForecastKey);
    } catch {}
    setUserForecastCfg(null);
  }

  const userForecastEffective = useMemo(() => {
    const cfg =
      userForecastCfg && typeof userForecastCfg === "object"
        ? userForecastCfg
        : {};
    const sc = cfg.scale || {};
    const ov = cfg.overrides || {};
    return {
      scale: {
        Oil: definedNum(Number(sc.Oil)) ? Number(sc.Oil) : 1,
        Gas: definedNum(Number(sc.Gas)) ? Number(sc.Gas) : 1,
        Water: definedNum(Number(sc.Water)) ? Number(sc.Water) : 1,
      },
      overrides: {
        Oil: ov.Oil && typeof ov.Oil === "object" ? ov.Oil : {},
        Gas: ov.Gas && typeof ov.Gas === "object" ? ov.Gas : {},
        Water: ov.Water && typeof ov.Water === "object" ? ov.Water : {},
      },
      editDays: definedNum(Number(cfg.editDays))
        ? clamp(Number(cfg.editDays), 7, 365)
        : 90,
    };
  }, [userForecastCfg]);

  const hasUserForecast = useMemo(() => {
    const sc = userForecastEffective.scale || {};
    if (sc.Oil !== 1 || sc.Gas !== 1 || sc.Water !== 1) return true;
    for (const s of STREAMS) {
      const o = userForecastEffective.overrides?.[s] || {};
      if (o && Object.keys(o).length) return true;
    }
    return false;
  }, [userForecastEffective]);

  function openUserForecastEditor() {
    // Start draft from effective config (so "cancel" is safe)
    setUserForecastDraft(JSON.parse(JSON.stringify(userForecastEffective)));
    setUserForecastOpen(true);
  }

  function saveUserForecastDraft() {
    if (!userForecastDraft) {
      setUserForecastOpen(false);
      return;
    }

    setUserForecastCfg(userForecastDraft);
    try {
      localStorage.setItem(userForecastKey, JSON.stringify(userForecastDraft));
    } catch {}

    // Activate user forecast after saving
    setUseUserForecast(true);

    setUserForecastOpen(false);
  }
  // Persist state back to URL without blowing away token/grant/etc.
  useEffect(() => {
    if (!init.isAdmin) return;

    const q = new URLSearchParams(window.location.search);

    // Keep OG params in sync too (so ogCharts doesn’t break)

    // NEVER write "~ALL~" back into URL. Only set LeaseID when we have a real lease.
    if (lease) q.set("LeaseID", lease);
    else q.delete("LeaseID");

    // Component-friendly params
    if (lease) q.set("lease", lease);
    else q.delete("lease");

    q.set("horizon", String(horizon));
    q.set("streams", streams.join(","));
    q.set("scale", logScale ? "log" : "linear");
    q.set("theme", dark ? "dark" : "light");
    q.set("bands", String(showBands));
    q.set("userForecast", String(forecastMode === "user"));
    q.set("zeroAsNull", String(zeroAsNull));
    q.set("cumulative", cumulativeMode);
    q.set("showMA", String(showMA));
    q.set("maWindow", String(maWindow));
    q.set("outliers", String(showOutliers));

    // Forecast mode
    // Forecast mode (derived from useUserForecast)
    if (useUserForecast) q.set("fmode", "user");
    else q.delete("fmode");
    if (selectedTags.length) q.set("tags", selectedTags.join(","));
    else q.delete("tags");

    // Tag filter params (TagID-only)
    if (selectedTags.length) {
      q.set("tags_mode", tagsMode);
      if (includeInactive) q.set("include_inactive", "1");
      else q.delete("include_inactive");
    } else {
      q.delete("tags_mode");
      q.delete("include_inactive");
    }

    // Keep legacy from/thru if already present (optional deep-linking)
    if (init.from) q.set("from", init.from);
    if (init.thru) q.set("thru", init.thru);

    // Ensure pass-through keys are present
    Object.entries(init.passThrough).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") q.set(k, v);
    });

    // IMPORTANT: no-restricted-globals fix
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${q.toString()}`,
    );
  }, [
    lease,
    horizon,
    streams,
    logScale,
    dark,
    showBands,
    forecastMode,
    zeroAsNull,
    cumulativeMode,
    showMA,
    maWindow,
    showOutliers,
    selectedTags,
    tagsMode,
    includeInactive,
    init,
  ]);

  // Load leases/tags
  useEffect(() => {
    if (!init.isAdmin) return;

    let alive = true;
    const ac = new AbortController();

    setLtLoading(true);
    setLtError(null);

    (async () => {
      try {
        const [leasesRes, tagsRes] = await Promise.all([
          fetch(init.LEASES_API, {
            signal: ac.signal,
            headers: { Accept: "application/json" },
          }),
          fetch(init.TAGS_API, {
            signal: ac.signal,
            headers: { Accept: "application/json" },
          }),
        ]);

        if (!leasesRes.ok) {
          const txt = await leasesRes.text().catch(() => "");
          throw new Error(
            `Leases API ${leasesRes.status}: ${txt || leasesRes.statusText}`,
          );
        }
        if (!tagsRes.ok) {
          const txt = await tagsRes.text().catch(() => "");
          throw new Error(
            `Tags API ${tagsRes.status}: ${txt || tagsRes.statusText}`,
          );
        }

        const leasesJson = await leasesRes.json();
        const tagsJson = await tagsRes.json();
        const rawLeases = Array.isArray(leasesJson?.Leases)
          ? leasesJson.Leases
          : [];

        if (!alive) return;

        const leases = rawLeases
          .map((l) => ({
            LeaseID: String(l?.LeaseID || "").trim(),
            LeaseName: String(l?.LeaseName || l?.LeaseID || "").trim(),
          }))
          .filter((l) => l.LeaseID && l.LeaseID !== "~ALL~");

        // usertags.php is a mixed array (tags + users + purchasers).
        // Keep ONLY objects that look like tags: {TagID, TagDesc}
        const rawTags = Array.isArray(tagsJson) ? tagsJson : [];
        const tags = rawTags
          .filter(
            (x) => x && typeof x === "object" && "TagID" in x && "TagDesc" in x,
          )
          .map((t) => ({
            TagID: String(t.TagID || "").trim(),
            TagDesc: String(t.TagDesc || "").trim(),
          }))
          .filter((t) => t.TagID && t.TagDesc);

        // Dedupe tags by TagID (keep first)
        const seen = new Set();
        const tagOptionsClean = [];
        for (const t of tags) {
          if (seen.has(t.TagID)) continue;
          seen.add(t.TagID);
          tagOptionsClean.push(t);
        }

        setLeaseOptions(leases);
        setTagOptions(tagOptionsClean);

        // If URL lease was missing or "~ALL~", default to first real lease.
        // ALSO: if URL lease was provided but not found in the list, fall back to first.
        setLease((prev) => {
          const prevRaw = String(prev || "").trim();
          const prevIsAll = prevRaw === "~ALL~";
          const hasPrev = Boolean(prevRaw) && !prevIsAll;

          const exists =
            hasPrev && leases.some((x) => String(x.LeaseID) === prevRaw);

          if (exists) return prevRaw;

          const first = leases?.[0]?.LeaseID;
          return first ? String(first) : "";
        });
      } catch (e) {
        if (!alive) return;
        if (e?.name === "AbortError") return;
        setLtError(e?.message || "Failed to load leases/tags");
      } finally {
        if (!alive) return;
        setLtLoading(false);
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [init]);

  // TAGID-ONLY: once tags load, drop any selected tag tokens that are not valid TagIDs
  // (This intentionally does NOT reconcile TagDesc => TagID)
  useEffect(() => {
    if (!tagOptions.length) return;

    const ids = new Set(tagOptions.map((t) => String(t.TagID)));
    setSelectedTags((prev) => {
      const next = (prev || [])
        .map((x) => String(x || "").trim())
        .filter((x) => x && ids.has(x));
      return Array.from(new Set(next));
    });
  }, [tagOptions]);

  async function load() {
    if (!init.isAdmin) return;

    // If NOT in tags mode, we require a real lease.
    if (!tagsActive && (!lease || lease === "~ALL~")) return;

    abortRef.current?.abort?.();
    abortRef.current = new AbortController();

    setLoading(true);
    setError(null);
    setLatencyMs(null);
    setRunAt(null);

    try {
      const t0 = performance.now();

      const params = {
        // Pass-through (if your API starts enforcing auth later, it's already here)
        token: init.passThrough.token || "",
        grant: init.passThrough.grant || "",
        UserID: init.passThrough.UserID || "",
        Company: init.passThrough.Company || "",
        Name: init.passThrough.Name || "",
        App: init.passThrough.App || "",

        horizon,
        streams: streams.join(","),
        limit: 250,
        include_llm: 1,
      };

      // Optional clamp params (actual/anomalies). Forecast is not clamped server-side.
      if (init.from) params.from = init.from;
      if (init.thru) params.thru = init.thru;

      if (tagsActive) {
        // TAGS MODE (TagID-only) => always request AGGREGATE payload
        params.tags = selectedTags.join(",");
        params.tags_mode = tagsMode || "any";
        params.aggregate = 1;
        if (includeInactive) params.include_inactive = 1;

        // IMPORTANT: do not send lease when tags are active (avoid implying single-lease)
        // (server doesn't need it, and it keeps share-links clean)
      } else {
        // SINGLE-LEASE MODE
        params.lease = lease;
      }

      const res = await fetchOverlay(params, abortRef.current.signal);
      const t1 = performance.now();

      setLatencyMs(Math.round(t1 - t0));
      setRunAt(new Date().toLocaleString());
      setData(res);

      if (res?.meta?.yScale) setLogScale(res.meta.yScale === "log");
    } catch (e) {
      if (e?.name !== "AbortError")
        setError(e?.message || "Failed to load overlay");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!init.isAdmin) return;
    if (didInitialLoadRef.current) return;

    // Initial load: allow tags-mode even if lease isn't picked yet
    if (!tagsActive && (!lease || lease === "~ALL~")) return;

    didInitialLoadRef.current = true;
    load(); /* initial */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [init.isAdmin, lease, tagsActive]);

  // Reload on parameter changes AFTER initial load
  useEffect(() => {
    if (!init.isAdmin) return;
    if (!didInitialLoadRef.current) return;
    if (!tagsActive && (!lease || lease === "~ALL~")) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lease, horizon, streams, selectedTags, tagsMode, includeInactive]);

  useEffect(() => {
    const userId = init.userIdRaw;
    const api = init.USERDETAILS_API;
    if (!userId || !api) return;

    const cacheKey = chartsPrefCacheKey({
      companySubdomain: init.companySubdomain,
      userId,
    });

    const cached = readChartsPrefCache(cacheKey);
    if (cached) setChartsPrefAll(mergeChartsPrefAll(cached));

    let alive = true;
    const ac = new AbortController();

    setChartsPrefLoading(true);
    setChartsPrefError(null);

    (async () => {
      try {
        const raw = await fetchChartsPrefFromUserDetails({
          api,
          userId,
          passThrough: init.passThrough,
          signal: ac.signal,
        });

        if (!alive) return;
        const merged = mergeChartsPrefAll(raw || cached || null);
        setChartsPrefAll(merged);
        writeChartsPrefCache(cacheKey, merged);
      } catch (e) {
        if (!alive) return;
        if (e?.name === "AbortError") return;

        const merged = mergeChartsPrefAll(cached || null);
        setChartsPrefAll(merged);
        setChartsPrefError(e?.message || "Failed to load chart prefs");
      } finally {
        if (!alive) return;
        setChartsPrefLoading(false);
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [init]);

  // Chart-ready "actual" (may be cumulative)
  const preparedActual = useMemo(() => {
    if (!data) return {};
    const out = {};
    for (const s of STREAMS) {
      const arr = (data.actual?.[s] || []).map((p) => {
        if (zeroAsNull && p.q === 0) return { ...p, q: null };
        return p;
      });
      out[s] = cumulativeMode === "on" ? cumulative(arr) : arr;
    }
    return out;
  }, [data, zeroAsNull, cumulativeMode]);
  // Load ChartsPref once (no spam)
  useEffect(() => {
    const apiBase = resolveApiBase();
    const userId = init.userIdRaw;
    if (!userId) return;

    let alive = true;
    (async () => {
      try {
        const raw = await fetchChartsPrefUserDetails({
          apiBase,
          userId,
          passThrough: init.passThrough,
        });
        if (!alive) return;

        const merged = {
          ...(raw && typeof raw === "object" ? raw : {}),
          v: Math.max(2, Number((raw && raw.v) || 1)),
          forecastDash: mergeForecastDashPrefs(raw?.forecastDash),
        };
        setChartsPrefAll(merged);
        setChartsPrefError(null);
      } catch (e) {
        if (!alive) return;
        setChartsPrefAll({
          v: 2,
          forecastDash: defaultForecastDashPrefs(),
        });
        setChartsPrefError(e?.message || "Failed to load chart prefs");
      }
    })();

    return () => {
      alive = false;
    };
  }, [init]);

  function openChartPrefsModal() {
    const fd = getForecastDashPrefsSafe(chartsPrefAll);
    setChartPrefsDraft(deepClone(fd));
    setChartPrefsTab("Oil");
    setChartPrefsPickerKey(null);
    setChartPrefsOpen(true);
  }

  function closeChartPrefsModal() {
    setChartPrefsOpen(false);
    setChartPrefsPickerKey(null);
  }

  function resetChartPrefsDraft() {
    setChartPrefsDraft(deepClone(defaultForecastDashPrefs()));
    setChartPrefsPickerKey(null);
  }

  function updateChartPrefField(stream, kind, field, value) {
    setChartPrefsDraft((prev) => {
      const next = deepClone(prev || defaultForecastDashPrefs());
      next.seriesPrefs = next.seriesPrefs || {};
      next.seriesPrefs[stream] = next.seriesPrefs[stream] || {};
      next.seriesPrefs[stream][kind] = next.seriesPrefs[stream][kind] || {};
      const v =
        field === "width" ||
        field === "opacity" ||
        field === "symbolSize" ||
        field === "barWidth"
          ? value === ""
            ? ""
            : Number(value)
          : value;
      next.seriesPrefs[stream][kind][field] = v;
      return next;
    });
  }

  function getPickerColor(draft, pickerKey) {
    if (!draft || !pickerKey) return "#000000";
    if (pickerKey.startsWith("base:")) {
      const stream = pickerKey.split(":")[1];
      return draft.colors?.[stream] || COLORS[stream] || "#000000";
    }
    if (pickerKey.startsWith("series:")) {
      const [, stream, kind] = pickerKey.split(":");
      const pref = draft.seriesPrefs?.[stream]?.[kind] || {};
      return (
        pref.color || draft.colors?.[stream] || COLORS[stream] || "#000000"
      );
    }
    return "#000000";
  }

  function handlePickerChange(hex) {
    setChartPrefsDraft((prev) => {
      const next = deepClone(prev || defaultForecastDashPrefs());
      const key = chartPrefsPickerKey;

      if (!key) return next;

      if (key.startsWith("base:")) {
        const stream = key.split(":")[1];
        next.colors = next.colors || {};
        next.colors[stream] = hex;
        return next;
      }

      if (key.startsWith("series:")) {
        const [, stream, kind] = key.split(":");
        next.seriesPrefs = next.seriesPrefs || {};
        next.seriesPrefs[stream] = next.seriesPrefs[stream] || {};
        next.seriesPrefs[stream][kind] = next.seriesPrefs[stream][kind] || {};
        next.seriesPrefs[stream][kind].color = hex;
        return next;
      }

      return next;
    });
  }

  // Single PATCH on Save (no “every change” server spam)
  async function saveChartPrefsDraft() {
    const apiBase = resolveApiBase();
    const userId = init.userIdRaw;
    if (!userId || !chartPrefsDraft) return;

    setChartsPrefSaving(true);
    try {
      const nextAll = {
        ...(chartsPrefAll && typeof chartsPrefAll === "object"
          ? chartsPrefAll
          : {}),
        v: Math.max(2, Number((chartsPrefAll && chartsPrefAll.v) || 1)),
        forecastDash: mergeForecastDashPrefs(chartPrefsDraft),
      };

      await patchChartsPrefUserDetails({
        apiBase,
        userId,
        passThrough: init.passThrough,
        chartsPrefAll: nextAll,
      });

      setChartsPrefAll(nextAll);
      setChartsPrefError(null);
      setChartPrefsOpen(false);
      setChartPrefsPickerKey(null);
    } catch (e) {
      setChartsPrefError(e?.message || "Failed to save chart prefs");
    } finally {
      setChartsPrefSaving(false);
    }
  }
  // Econ should ALWAYS use daily series (not cumulative)
  const actualForEcon = useMemo(() => {
    if (!data) return {};
    const a = data.actual || {};
    if (!zeroAsNull) return a;

    // Convert zeros to null to avoid huge slopes
    const out = {};
    for (const s of STREAMS) {
      const pts = a?.[s]?.map((p) => ({
        date: p.date,
        q: p.q === 0 ? null : p.q,
      }));
      out[s] = pts;
    }
    return out;
  }, [data, zeroAsNull]);

  // Forecast dates (base) for editor/export
  const forecastDates = useMemo(() => {
    if (!data) return [];
    const all = gatherAllDates({}, data.forecast);
    const start = data?.meta?.forecast_start || all[0] || "";
    if (!start) return all;
    const t0 = +parseISO(start);
    return all.filter((d) => +parseISO(d) >= t0);
  }, [data]);

  const ufEditDates = useMemo(() => {
    const d = userForecastDraft || userForecastEffective;
    const days = clamp(Number(d?.editDays ?? 90), 7, 365);
    return (forecastDates || []).slice(0, days);
  }, [forecastDates, userForecastDraft, userForecastEffective]);

  const baseForecastMidMaps = useMemo(() => {
    if (!data) return { Oil: new Map(), Gas: new Map(), Water: new Map() };
    return {
      Oil: seriesMapForecastMid(data.forecast?.Oil),
      Gas: seriesMapForecastMid(data.forecast?.Gas),
      Water: seriesMapForecastMid(data.forecast?.Water),
    };
  }, [data]);
  const forecastUser = useMemo(() => {
    if (!data) return {};
    const out = { ...(data.forecast || {}) };

    for (const s of STREAMS) {
      const baseEntry = data.forecast?.[s];
      if (!baseEntry) continue;

      const { mid, p10, p90 } = extractForecast(baseEntry);

      const scale = Number(userForecastEffective.scale?.[s] ?? 1);
      const overrides = userForecastEffective.overrides?.[s] || {};

      const p50 = (mid || []).map(([d, v]) => {
        let q = definedNum(v) ? Number(v) : v;
        if (definedNum(q)) q = q * (definedNum(scale) ? scale : 1);

        const ov = overrides?.[d];
        if (ov !== undefined) {
          if (ov === null || ov === "") {
            // treat null/"" as "remove override" => keep scaled base
          } else {
            const n = Number(ov);
            if (definedNum(n)) q = n;
          }
        }

        return { date: d, q };
      });

      // Keep bands (p10/p90) from the base DCA forecast.
      out[s] = {
        p10: (p10 || []).map(([d, v]) => ({ date: d, q: v })),
        p50,
        p90: (p90 || []).map(([d, v]) => ({ date: d, q: v })),
      };
    }

    return out;
  }, [data, userForecastEffective]);

  // What the charts + econ should use RIGHT NOW (base vs user)
  const forecastView = useMemo(() => {
    if (!data) return {};
    return forecastMode === "user" ? forecastUser : data.forecast || {};
  }, [data, forecastMode, forecastUser]);

  function handleExportUserForecastCSV() {
    if (!data) return;

    const f = forecastUser || {};
    const header = ["date", ...STREAMS];
    const rows = [header.join(",")];

    const forecastForEcon =
      forecastMode === "user" ? forecastUser : data?.forecast || {};

    const midMaps = {
      Oil: seriesMapForecastMid(forecastForEcon?.Oil),
      Gas: seriesMapForecastMid(forecastForEcon?.Gas),
      Water: seriesMapForecastMid(forecastForEcon?.Water),
    };

    // Export only the forecast window dates
    for (const d of forecastDates || []) {
      const row = [d];
      for (const s of STREAMS) {
        row.push(midMaps?.[s]?.get?.(d) ?? "");
      }
      rows.push(
        row.map((x) => (typeof x === "string" ? csvEscape(x) : x)).join(","),
      );
    }

    const blob = new Blob([rows.join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    const key = tagsActive
      ? `tags_${selectedTags.join("-")}`
      : `${lease || "lease"}`;
    a.download = `${init.companyLabel
      .toLowerCase()
      .replace(/\s+/g, "_")}_user_p50_${key}_${iso(new Date())}.csv`;
    a.click();
  }

  function handleImportUserForecastFile(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const textCsv = String(reader.result || "");
      const parsedOverrides = parseUserForecastOverridesCSV(textCsv);
      if (!parsedOverrides) return;

      setUserForecastDraft((prev) => {
        const base = prev
          ? JSON.parse(JSON.stringify(prev))
          : JSON.parse(JSON.stringify(userForecastEffective));

        base.scale = base.scale || { Oil: 1, Gas: 1, Water: 1 };
        base.overrides = base.overrides || { Oil: {}, Gas: {}, Water: {} };

        for (const s of STREAMS) {
          base.overrides[s] = {
            ...(base.overrides[s] || {}),
            ...(parsedOverrides[s] || {}),
          };
        }
        return base;
      });
    };
    reader.readAsText(file);
  }

  const leaseNameById = useMemo(() => {
    const m = new Map();
    leaseOptions.forEach((l) => m.set(l.LeaseID, l.LeaseName));
    return m;
  }, [leaseOptions]);

  const tagDescById = useMemo(() => {
    const m = new Map();
    tagOptions.forEach((t) => m.set(String(t.TagID), t.TagDesc));
    return m;
  }, [tagOptions]);

  function minForYAxis() {
    if (!data) return logScale ? 0.1 : "dataMin";
    const values = [];
    for (const s of STREAMS) {
      (preparedActual[s] || []).forEach(
        (p) => definedNum(p?.q) && values.push(p.q),
      );
      const fEntry = forecastView?.[s];
      const { mid, p10, p90 } = extractForecast(fEntry);
      [...mid, ...p10, ...p90].forEach(
        ([, v]) => definedNum(v) && values.push(v),
      );
    }
    const min = Math.min(...values.filter((v) => v > 0));
    if (!Number.isFinite(min)) return logScale ? 0.1 : "dataMin";
    if (!logScale) return Math.max(0, Math.floor(min - 0.1 * Math.abs(min)));
    return Math.max(0.01, min * 0.8);
  }

  const domain = useMemo(() => {
    if (!data) return { min: "", max: "" };
    const all = gatherAllDates(preparedActual, forecastView);
    return {
      min: all[0] || data?.meta?.forecast_start || "",
      max: all[all.length - 1] || data?.meta?.forecast_end || "",
    };
  }, [data, preparedActual, forecastView]);

  const cards = useMemo(() => {
    const out = [];
    if (!data) return out;

    for (const s of STREAMS) {
      const lastPt = latest(preparedActual[s] || []);
      const last = lastPt?.q;
      const lastDate = lastPt?.date;
      const avg7 = averageOfLast(preparedActual[s] || [], 7) ?? undefined;
      const delta =
        definedNum(last) && definedNum(avg7) ? (last - avg7) / avg7 : undefined;

      const fEntry = forecastView?.[s];
      let next7;
      if (fEntry) {
        const { mid } = extractForecast(fEntry);
        if (mid?.length) {
          const startAfter = data?.meta?.forecast_start || iso(new Date());
          const filtered = mid
            .filter(([d]) => d >= startAfter)
            .slice(0, 7)
            .map(([, v]) => v || 0);
          if (filtered.length)
            next7 = filtered.reduce((a, b) => a + (b || 0), 0);
        }
      }

      out.push({ stream: s, last, lastDate, avg7, delta, next7 });
    }
    return out;
  }, [data, preparedActual, forecastView]);

  // ECON computed window (forward from Thru for N days) + decision metrics + scenarios
  const econBundle = useMemo(() => {
    if (!data) return null;

    const all = gatherAllDates(actualForEcon, forecastView);
    if (!all.length) return null;

    const start = data?.meta?.forecast_start || domain.min || iso(new Date());
    const days = Math.max(7, parseInt(econ.unprofitableSoonDays || "60", 10));
    const forward = all.filter((d) => d >= start).slice(0, days);

    const aOil = seriesMapActual(actualForEcon.Oil || []);
    const aGas = seriesMapActual(actualForEcon.Gas || []);
    const aWat = seriesMapActual(actualForEcon.Water || []);

    const fOil = seriesMapForecastMid(forecastView?.Oil);
    const fGas = seriesMapForecastMid(forecastView?.Gas);
    const fWat = seriesMapForecastMid(forecastView?.Water);

    function computeForEcon(econUse) {
      // Capex schedule
      const capexDate = (econUse.capexDate || "").trim() || start;
      const abandonmentDate =
        (econUse.abandonmentDate || "").trim() ||
        forward[forward.length - 1] ||
        start;

      let rows = forward.map((d) => {
        const oil = definedNum(fOil.get(d)) ? fOil.get(d) : aOil.get(d);
        const gas = definedNum(fGas.get(d)) ? fGas.get(d) : aGas.get(d);
        const wat = definedNum(fWat.get(d)) ? fWat.get(d) : aWat.get(d);

        let capex = 0;
        if (definedNum(econUse.capexUpfront) && econUse.capexUpfront !== 0) {
          if (d === capexDate) capex += Number(econUse.capexUpfront) || 0;
        }
        if (
          definedNum(econUse.abandonmentCost) &&
          econUse.abandonmentCost !== 0
        ) {
          if (d === abandonmentDate)
            capex += Number(econUse.abandonmentCost) || 0;
        }

        return econRow({
          date: d,
          oil,
          gas,
          water: wat,
          econ: econUse,
          capex,
          startDate: start,
        });
      });

      // Cumulative & decision metrics
      let cum = 0;
      rows = rows.map((r) => {
        cum += definedNum(r.cashflow) ? r.cashflow : 0;
        return { ...r, cumCashflow: cum };
      });

      const firstNeg = firstNegative(rows);
      const rollNeg = rollingAvgNegativeDate(
        rows,
        econUse.econLimitRollingDays,
      );
      const consNeg = consecutiveNegativeDate(
        rows,
        econUse.econLimitConsecutiveNegDays,
      );

      // Economic limit: earliest of rolling negative avg or consecutive negatives
      const econLimit = [rollNeg, consNeg].filter(Boolean).sort()[0] || "";

      const pv = sumOf(rows, "pv");
      const totalCash = sumOf(rows, "cashflow");
      const totalBoe = sumOf(rows, "boe");
      const totalOpex = sumOf(rows, "opex");
      const totalNgl = sumOf(rows, "ngl");
      const totalGrossNgl = sumOf(rows, "grossNgl");
      const netback = totalBoe > 0 ? totalCash / totalBoe : null;
      const loePerBoe = totalBoe > 0 ? totalOpex / totalBoe : null;
      const pay = payoutDate(rows);

      // Decision label
      const soonDays = Math.max(
        7,
        parseInt(econUse.unprofitableSoonDays || "60", 10),
      );
      const dFirstNeg = firstNeg ? daysBetweenISO(start, firstNeg) : null;
      const dLimit = econLimit ? daysBetweenISO(start, econLimit) : null;

      let decision = "Operate";
      let decisionDetail = "Cashflow stays positive under current assumptions.";
      if (
        econLimit &&
        definedNum(dLimit) &&
        dLimit >= 0 &&
        dLimit <= soonDays
      ) {
        decision = "Shut-in candidate";
        decisionDetail = `Economic limit hits in ~${dLimit} days (by rules).`;
      } else if (
        firstNeg &&
        definedNum(dFirstNeg) &&
        dFirstNeg >= 0 &&
        dFirstNeg <= soonDays
      ) {
        decision = "Monitor";
        decisionDetail = `First negative cashflow appears in ~${dFirstNeg} days.`;
      }

      return {
        start,
        days: soonDays,
        rows,
        firstNegDate: firstNeg,
        econLimitDate: econLimit,
        rollingNegDate: rollNeg,
        consecutiveNegDate: consNeg,
        payoutDate: pay,
        pv,
        totalCash,
        totalNgl,
        totalGrossNgl,
        netback,
        loePerBoe,
        decision,
        decisionDetail,
      };
    }

    // Compute scenarios
    const scenarios = {};
    for (const sc of ECON_SCENARIOS) {
      const econUse = applyScenario(econ, sc);
      scenarios[sc.key] = {
        key: sc.key,
        name: sc.name,
        config: sc,
        econUse,
        result: computeForEcon(econUse),
      };
    }

    // Selected scenario
    const selected = scenarios[econScenarioKey] || scenarios.Base;

    // Breakevens on selected scenario (PV=0 and min CF>=0)
    const baseEcon = selected.econUse;
    const computePVAtOil = (oilPrice) => {
      const e2 = { ...baseEcon, oilPrice };
      return computeForEcon(e2).pv;
    };
    const computeMinCFAtOil = (oilPrice) => {
      const e2 = { ...baseEcon, oilPrice };
      const res = computeForEcon(e2);
      const mins = res.rows.map((r) =>
        definedNum(r.cashflow) ? r.cashflow : 0,
      );
      return Math.min(...mins);
    };
    const computePVAtFixedLoe = (fixedLoePerMonth) => {
      const e2 = { ...baseEcon, fixedLoePerMonth };
      return computeForEcon(e2).pv;
    };

    const beOilPV0 = solveBisection({
      lo: 0,
      hi: 200,
      fn: computePVAtOil,
      target: 0,
      maxIter: 40,
      eps: 1e-3,
    });

    const beOilMinCF0 = solveBisection({
      lo: 0,
      hi: 250,
      fn: computeMinCFAtOil,
      target: 0,
      maxIter: 40,
      eps: 1e-2,
    });

    const maxFixedLoePV0 = solveBisection({
      lo: 0,
      hi: Math.max(1000, (Number(baseEcon.fixedLoePerMonth) || 0) * 4),
      fn: computePVAtFixedLoe,
      target: 0,
      maxIter: 40,
      eps: 1e-2,
    });

    return {
      scenarios,
      selectedKey: selected.key,
      selectedName: selected.name,
      selectedEcon: baseEcon,
      selectedResult: selected.result,
      breakevens: {
        beOilPV0,
        beOilMinCF0,
        maxFixedLoePV0,
      },
    };
  }, [
    data,
    actualForEcon,
    forecastMode, // "base" | "user"
    userForecastEffective, // edits should trigger recompute
    econ,
    econScenarioKey,
    ECON_SCENARIOS,
    domain.min,
  ]);
  const option = useMemo(() => {
    const themeText = dark ? "#e5e7eb" : "#111827";
    const series = [];

    if (data) {
      const forecastToPlot = forecastView || {};
      const allF = gatherAllDates({}, forecastView);
      const forecastStart = allF[0];
      const lastDate = domain.max;

      if (forecastStart && lastDate) {
        series.push({
          name: "Forecast Window",
          type: "line",
          data: [],
          silent: true,
          markArea: {
            itemStyle: {
              color: dark
                ? { type: "linear", x: 0, y: 0, x2: 1, y2: 0,
                    colorStops: [
                      { offset: 0, color: "rgba(30, 58, 138, 0.08)" },
                      { offset: 0.5, color: "rgba(59, 130, 246, 0.06)" },
                      { offset: 1, color: "rgba(30, 58, 138, 0.03)" },
                    ] }
                : "rgba(99,102,241,0.10)",
            },
            data: [[{ xAxis: forecastStart }, { xAxis: lastDate }]],
          },
          tooltip: { show: false },
        });
      }

      const today = iso(new Date());
      series.push({
        name: "Today",
        type: "line",
        data: [],
        silent: true,
        markLine: {
          symbol: "none",
          label: {
            show: true,
            formatter: "TODAY",
            position: "start",
            color: dark ? "#22d3ee" : "#0891b2",
            fontWeight: 800,
            fontSize: 11,
            fontFamily: "'JetBrains Mono', 'SF Mono', Consolas, monospace",
            letterSpacing: "0.08em",
            backgroundColor: "transparent",
            padding: [2, 0, 0, 0],
          },
          lineStyle: {
            type: "solid",
            color: dark ? "#22d3ee" : "#0891b2",
            width: 2,
            shadowColor: dark ? "rgba(34, 211, 238, 0.4)" : "transparent",
            shadowBlur: dark ? 8 : 0,
          },
          data: [{ xAxis: today }],
        },
        tooltip: { show: false },
      });

      // --- Chart-native annotations via markPoint on a phantom series ---
      if (dark && forecastStart) {
        const allDates = gatherAllDates(preparedActual, forecastView);
        const dateCount = allDates.length;
        // Place annotations at approximate chart regions
        const pickDate = (frac) => allDates[Math.min(Math.floor(dateCount * frac), dateCount - 1)] || today;
        const annotationSeries = {
          name: "_annotations",
          type: "line",
          data: [],
          silent: true,
          tooltip: { show: false },
          markPoint: {
            symbol: "circle",
            symbolSize: 0,
            label: {
              show: true,
              fontFamily: "'JetBrains Mono', 'SF Mono', Consolas, monospace",
              fontSize: 9,
              fontWeight: 600,
              color: "rgba(148, 163, 184, 0.7)",
              backgroundColor: "rgba(15, 23, 42, 0.6)",
              borderRadius: 3,
              padding: [3, 6],
            },
            data: [
              { name: "Peak Profitability", coord: [pickDate(0.25), null], label: { formatter: "● Peak Profitability", color: "rgba(52, 211, 153, 0.8)" }, symbolSize: 0, value: "" },
              { name: "Decline Acceleration", coord: [pickDate(0.42), null], label: { formatter: "● Decline Acceleration", color: "rgba(248, 113, 113, 0.8)" }, symbolSize: 0, value: "" },
              { name: "Recovery Zone", coord: [pickDate(0.65), null], label: { formatter: "● Recovery Zone", color: "rgba(59, 130, 246, 0.8)" }, symbolSize: 0, value: "" },
              { name: "Critical Decision Point", coord: [pickDate(0.78), null], label: { formatter: "● Critical Decision Point", color: "rgba(251, 191, 36, 0.8)" }, symbolSize: 0, value: "" },
            ],
          },
        };
        series.push(annotationSeries);
      }

      const fd = getForecastDashPrefsSafe(chartsPrefAll);
      const streamColors = fd.colors || COLORS;

      for (const s of STREAMS) {
        if (!streams.includes(s)) continue;

        const baseColor = streamColors[s] || COLORS[s];
        const ptsA = (preparedActual[s] || []).map(toPair).filter(Boolean);

        const fEntry = forecastToPlot?.[s];
        const { mid: fMid, p10, p90, hasBand } = extractForecast(fEntry);

        // --- Actual ---
        series.push(
          applySeriesPrefToEchartsSeries(
            {
              name: `${s} Actual`,
              type: "scatter",
              symbolSize: dark ? 4 : 5,
              data: ptsA,
              itemStyle: {
                color: baseColor,
                opacity: dark ? 0.85 : 1,
                shadowColor: dark ? hexToRgba(baseColor, 0.25) : "transparent",
                shadowBlur: dark ? 3 : 0,
              },
              emphasis: { itemStyle: { borderColor: dark ? "#e2e8f0" : "#000", borderWidth: 1, shadowBlur: 6 } },
              animation: false,
              z: 3,
            },
            pickFdSeriesPref(fd, s, "Actual"),
            { defaultColor: baseColor },
          ),
        );

        // --- Moving Avg ---
        if (showMA) {
          series.push(
            applySeriesPrefToEchartsSeries(
              {
                name: `${s} ${maWindow}D Avg`,
                type: "line",
                showSymbol: false,
                smooth: 0.35,
                lineStyle: {
                  width: dark ? 2.5 : 2,
                  opacity: 0.95,
                  shadowColor: dark ? hexToRgba(baseColor, 0.35) : "transparent",
                  shadowBlur: dark ? 8 : 0,
                },
                itemStyle: { color: baseColor },
                data: movingAverage(preparedActual[s] || [], maWindow),
                z: 2,
              },
              pickFdSeriesPref(fd, s, "MA"),
              { defaultColor: baseColor },
            ),
          );
        }

        const isUserMode = forecastMode === "user";

        // --- Bands ---
        if (!isUserMode && showBands && hasBand && p10.length && p90.length) {
          const fillAlpha = dark ? fd.bandFillAlphaDark : fd.bandFillAlphaLight;

          const bandColor = p10?.length > 0
            ? (pickFdSeriesPref(fd, s, "P10")?.color || baseColor)
            : baseColor;

          const p10Series = applySeriesPrefToEchartsSeries(
            {
              name: `${s} p10`,
              type: "line",
              showSymbol: false,
              lineStyle: { opacity: dark ? 0.2 : 0, width: dark ? 1 : 0, type: "dotted", color: hexToRgba(bandColor, 0.4) },
              itemStyle: { color: bandColor },
              data: p10,
              z: 1,
              silent: true,
            },
            pickFdSeriesPref(fd, s, "P10"),
            { defaultColor: baseColor },
          );

          const p90Series = applySeriesPrefToEchartsSeries(
            {
              name: `${s} p10–p90`,
              type: "line",
              showSymbol: false,
              data: p90,
              lineStyle: { opacity: dark ? 0.2 : 0, width: dark ? 1 : 0, type: "dotted", color: hexToRgba(bandColor, 0.4) },
              areaStyle: {
                color: dark
                  ? { type: "linear", x: 0, y: 0, x2: 0, y2: 1,
                      colorStops: [
                        { offset: 0, color: hexToRgba(bandColor, fillAlpha * 1.2) },
                        { offset: 0.5, color: hexToRgba(bandColor, fillAlpha * 0.6) },
                        { offset: 1, color: hexToRgba(bandColor, fillAlpha * 0.2) },
                      ] }
                  : hexToRgba(bandColor, fillAlpha),
              },
              z: 1,
              silent: true,
            },
            pickFdSeriesPref(fd, s, "P90"),
            { defaultColor: baseColor },
          );

          series.push(p10Series);
          series.push(p90Series);
        }

        // --- Forecast Mid (p50) ---
        const midKind = isUserMode ? "User Forecast(p50)" : "Forecast(p50)";
        const midName = isUserMode
          ? `${s} User Forecast (p50)`
          : `${s} Forecast (p50)`;

        series.push(
          applySeriesPrefToEchartsSeries(
            {
              name: midName,
              type: "line",
              showSymbol: false,
              smooth: dark ? 0.3 : false,
              data: fMid,
              lineStyle: isUserMode
                ? { type: "solid", width: 2.5, shadowColor: dark ? hexToRgba(baseColor, 0.3) : "transparent", shadowBlur: dark ? 6 : 0 }
                : { type: "dashed", width: 2, shadowColor: dark ? hexToRgba(baseColor, 0.2) : "transparent", shadowBlur: dark ? 4 : 0 },
              itemStyle: { color: baseColor },
              z: 2,
            },
            pickFdSeriesPref(fd, s, midKind),
            { defaultColor: baseColor },
          ),
        );

        // --- Outliers / Anomalies ---
        if (showOutliers) {
          const outliers = (outlierPoints?.[s] || []).map((p) => [
            p.date,
            p.value,
            p.score,
          ]);
          if (outliers.length) {
            series.push(
              applySeriesPrefToEchartsSeries(
                {
                  name: `${s} Outliers`,
                  type: "scatter",
                  symbol: "triangle",
                  symbolSize: (val) => {
                    const score = Math.abs(val?.[2] ?? 0);
                    return Math.max(8, Math.min(18, 8 + score * 10));
                  },
                  data: outliers,
                  itemStyle: { color: dark ? "#fbbf24" : "#f59e0b" },
                  z: 4,
                },
                pickFdSeriesPref(fd, s, "Outliers"),
                { defaultColor: dark ? "#fbbf24" : "#f59e0b" },
              ),
            );
          }

          const anPts = (anomalyPoints?.[s] || []).map((p) => [
            p.date,
            p.value,
            p.score,
          ]);
          if (anPts.length) {
            series.push(
              applySeriesPrefToEchartsSeries(
                {
                  name: `${s} Anomalies`,
                  type: "scatter",
                  symbol: "pin",
                  symbolSize: (val) => {
                    const score = Math.abs(val?.[2] ?? 0);
                    return Math.max(10, Math.min(22, 10 + score * 14));
                  },
                  data: anPts,
                  itemStyle: { color: dark ? "#f87171" : "#dc2626" },
                  z: 5,
                },
                pickFdSeriesPref(fd, s, "Anomalies"),
                { defaultColor: dark ? "#f87171" : "#dc2626" },
              ),
            );
          }
        }
      }
    }

    function openChartPrefsModal() {
      setChartPrefsDraft(getForecastDashPrefsSafe(chartsPrefAll));
      setChartPrefsOpen(true);
    }

    function openChartPrefs() {
      openChartPrefsModal();
    }

    function closeChartPrefsModal() {
      setChartPrefsOpen(false);
      setChartPrefsPickerKey(null);
    }

    function resetChartPrefsDraft() {
      setChartPrefsDraft(getForecastDashPrefsSafe(chartsPrefAll));
    }

    async function saveChartPrefsDraft() {
      if (!chartPrefsDraft) return;

      try {
        setChartsPrefSaving(true);
        setChartsPrefError(null);

        const merged = mergeChartsPrefAll({
          ...(chartsPrefAll || {}),
          forecastDash: chartPrefsDraft,
        });

        await patchChartsPrefToUserDetails({
          api: init.USERDETAILS_API,
          userId: init.userIdRaw,
          passThrough: init.passThrough,
          chartsPrefAll: merged,
        });

        setChartsPrefAll(merged);
        closeChartPrefsModal();
      } catch (e) {
        setChartsPrefError(e.message || "Failed to save");
      } finally {
        setChartsPrefSaving(false);
      }
    }
    function updateChartPrefField(stream, kind, field, value) {
      setChartPrefsDraft((prev) => {
        const next = deepClone(prev || {});
        if (!next.seriesPrefs) next.seriesPrefs = {};
        if (!next.seriesPrefs[stream]) next.seriesPrefs[stream] = {};
        if (!next.seriesPrefs[stream][kind])
          next.seriesPrefs[stream][kind] = {};

        next.seriesPrefs[stream][kind][field] = value;
        return next;
      });
    }
    function getPickerColor(stream, kind) {
      const pref = chartPrefsDraft?.seriesPrefs?.[stream]?.[kind] || {};
      return (
        pref.color ||
        chartsPrefAll?.forecastDash?.colors?.[stream] ||
        COLORS[stream]
      );
    }

    function handlePickerChange(stream, kind, color) {
      updateChartPrefField(stream, kind, "color", color.hex);
    }
    const outlierPoints = (() => {
      if (!data?.actual) return {};
      const map = {};
      for (const s of STREAMS) {
        const dates = outlierDates(data.actual[s] || []);
        map[s] = (data.actual[s] || [])
          .filter((p) => dates.has(p.date))
          .map((p) => [p.date, p.q]);
      }
      return map;
    })();

    const anomalyPoints = (() => {
      if (!data?.anomalies) return {};
      const map = {};
      for (const s of STREAMS) {
        map[s] = (data.anomalies[s] || [])
          .filter(shouldPlotAnomaly)
          .map((a) => [a.date, a.value]);
      }
      return map;
    })();
    // Premium cockpit chart palette
    const ckBg = dark ? "#060a13" : "#ffffff";
    const ckGrid = dark ? "rgba(30, 50, 90, 0.15)" : "#e5e7eb";
    const ckGridMinor = dark ? "rgba(30, 50, 90, 0.08)" : "#f1f5f9";
    const ckAxis = dark ? "rgba(45, 70, 120, 0.3)" : "#e5e7eb";
    const ckLabel = dark ? "#94a3b8" : "#111827";
    const ckLabelDim = dark ? "#64748b" : "#6b7280";

    return {
      backgroundColor: ckBg,
      textStyle: { color: ckLabel, fontFamily: "'Inter', -apple-system, sans-serif" },
      grid: { left: 56, right: 20, top: 52, bottom: 56, containLabel: true },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
          lineStyle: { color: dark ? "rgba(34, 211, 238, 0.3)" : "#94a3b8", type: "dashed", width: 1 },
          crossStyle: { color: dark ? "rgba(34, 211, 238, 0.2)" : "#94a3b8" },
        },
        backgroundColor: dark
          ? "rgba(10, 16, 32, 0.94)"
          : "rgba(255,255,255,0.96)",
        borderColor: dark ? "rgba(45, 70, 120, 0.35)" : "#e5e7eb",
        borderWidth: 1,
        textStyle: {
          color: dark ? "#e2e8f0" : "#111827",
          fontSize: 11,
          fontFamily: "'Inter', -apple-system, sans-serif",
        },
        extraCssText: dark
          ? "backdrop-filter:blur(8px);box-shadow:0 8px 32px rgba(0,0,0,0.5);border-radius:6px;"
          : "box-shadow:0 4px 16px rgba(0,0,0,0.1);border-radius:6px;",
        confine: true,
        formatter: (params) => {
          if (!params?.length) return "";
          const d = params[0].axisValueLabel;
          const lines = [
            `<div style="font-weight:700;margin-bottom:4px;font-family:'JetBrains Mono',monospace;font-size:10px;color:${dark ? "#94a3b8" : "#6b7280"};letter-spacing:0.04em">${d}</div>`,
          ];
          for (const p of params) {
            if (p.seriesName?.startsWith("_")) continue; // skip annotation phantom series
            const stream = STREAMS.find((s) => p.seriesName?.startsWith(s));
            const unit = stream ? unitsByStream[stream] || "" : "";
            const bullet = `<span style="display:inline-block;margin-right:6px;border-radius:50%;width:7px;height:7px;background:${p.color};box-shadow:0 0 4px ${p.color}"></span>`;
            const val = p.data?.[1] ?? p.value ?? null;
            lines.push(
              `<div style="display:flex;align-items:center;gap:2px;margin:2px 0">${bullet}<span style="font-size:11px">${p.seriesName}:</span> <b style="font-family:'JetBrains Mono',monospace;font-variant-numeric:tabular-nums">${withUnits(Number(val), unit)}</b></div>`,
            );
          }
          return lines.join("");
        },
      },
      legend: {
        top: 6,
        type: "scroll",
        textStyle: { color: ckLabelDim, fontSize: 10, fontFamily: "'Inter', sans-serif" },
        pageIconColor: ckLabelDim,
        pageTextStyle: { color: ckLabelDim },
        itemWidth: 12,
        itemHeight: 8,
        itemGap: 14,
      },
      xAxis: {
        type: "time",
        min: domain.min || undefined,
        max: domain.max || undefined,
        axisLine: { lineStyle: { color: ckAxis } },
        axisTick: { lineStyle: { color: ckAxis } },
        axisLabel: { color: ckLabelDim, fontSize: 10, fontFamily: "'JetBrains Mono', monospace" },
        splitLine: {
          show: true,
          lineStyle: { color: ckGrid, type: "dashed" },
        },
      },
      yAxis: {
        type: logScale ? "log" : "value",
        min: minForYAxis(),
        minorTick: { show: logScale, lineStyle: { color: ckGridMinor } },
        minorSplitLine: { show: logScale, lineStyle: { color: ckGridMinor } },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: ckLabelDim,
          fontSize: 10,
          fontFamily: "'JetBrains Mono', monospace",
          formatter: (v) => (v >= 1000 ? `${fmt.format(v)}` : fmt.format(v)),
        },
        splitLine: {
          lineStyle: { color: ckGrid, type: "dashed" },
        },
      },
      dataZoom: [
        { type: "inside", throttle: 50, start: 0, end: 100 },
        {
          type: "slider", height: 18, bottom: 20, start: 0, end: 100,
          borderColor: dark ? "rgba(30, 50, 90, 0.3)" : "#e5e7eb",
          backgroundColor: dark ? "rgba(10, 16, 32, 0.6)" : "#f8fafc",
          fillerColor: dark ? "rgba(34, 211, 238, 0.08)" : "rgba(59, 130, 246, 0.1)",
          handleStyle: { color: dark ? "#1e3a8a" : "#3b82f6", borderColor: dark ? "#3b82f6" : "#2563eb" },
          dataBackground: {
            lineStyle: { color: dark ? "rgba(59, 130, 246, 0.3)" : "#93c5fd" },
            areaStyle: { color: dark ? "rgba(59, 130, 246, 0.06)" : "rgba(59, 130, 246, 0.05)" },
          },
          textStyle: { color: ckLabelDim, fontSize: 9, fontFamily: "'JetBrains Mono', monospace" },
        },
      ],
      series,
      animation: false,
    };
  }, [
    data,
    logScale,
    streams,
    showMA,
    maWindow,
    showOutliers,
    showBands,
    dark,
    preparedActual,
    domain,
    unitsByStream,
  ]);

  const econChartOption = useMemo(() => {
    const res = econBundle?.selectedResult;
    if (!res?.rows?.length) return null;

    const themeText = dark ? "#e5e7eb" : "#111827";
    const rows = res.rows;

    const cf = rows.map((r) => [
      r.date,
      definedNum(r.cashflow) ? r.cashflow : 0,
    ]);
    const cum = rows.map((r) => [
      r.date,
      definedNum(r.cumCashflow) ? r.cumCashflow : 0,
    ]);
    const pv = rows.map((r) => [r.date, definedNum(r.pv) ? r.pv : 0]);

    return {
      backgroundColor: dark ? "#0b1220" : "#ffffff",
      textStyle: { color: themeText },
      grid: { left: 64, right: 24, top: 48, bottom: 40, containLabel: true },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        confine: true,
        backgroundColor: dark
          ? "rgba(17,24,39,0.92)"
          : "rgba(255,255,255,0.96)",
        textStyle: { color: themeText },
        formatter: (params) => {
          if (!params?.length) return "";
          const d = params[0].axisValueLabel;
          const lines = [
            `<div style="font-weight:700;margin-bottom:4px">${d}</div>`,
          ];
          for (const p of params) {
            const val = Number(p.data?.[1] ?? 0);
            lines.push(`${p.seriesName}: <b>${money(val, 0)}</b>`);
          }
          return lines.join("<br/>");
        },
      },
      legend: {
        top: 8,
        textStyle: { color: themeText },
      },
      xAxis: {
        type: "time",
        axisLine: { lineStyle: { color: dark ? "#1f2937" : "#e5e7eb" } },
        axisLabel: { color: themeText },
        splitLine: {
          lineStyle: { color: dark ? "#1f2937" : "#e5e7eb", opacity: 0.25 },
        },
      },
      yAxis: [
        {
          type: "value",
          axisLabel: { color: themeText },
          splitLine: {
            lineStyle: {
              color: dark ? "#1f2937" : "#e5e7eb",
              opacity: 0.25,
            },
          },
        },
      ],
      series: [
        {
          name: "Daily Cashflow",
          type: "bar",
          data: cf,
        },
        {
          name: "Cumulative Cashflow",
          type: "line",
          showSymbol: false,
          data: cum,
        },
        {
          name: "Discounted CF (PV stream)",
          type: "line",
          showSymbol: false,
          lineStyle: { type: "dashed" },
          data: pv,
        },
      ],
      animation: false,
    };
  }, [econBundle, dark]);

  function handleExportPNG() {
    const inst = chartRef.current?.getEchartsInstance?.();
    if (!inst) return;
    const url = inst.getDataURL({
      type: "png",
      pixelRatio: 3,
      backgroundColor: dark ? "#0b1220" : "#ffffff",
    });
    const a = document.createElement("a");
    a.href = url;

    const key = tagsActive
      ? `tags_${selectedTags.join("-")}`
      : `${lease || "lease"}`;

    a.download = `${init.companyLabel
      .toLowerCase()
      .replace(/\s+/g, "_")}_forecast_${key}_${iso(new Date())}.png`;
    a.click();
  }

  function handleExportCSV() {
    if (!data) return;
    const blob = new Blob([toCSV(data)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
  }

  function handleExportUserForecastCSV(datesHint) {
    if (!data) return;
    const blob = new Blob(
      [userForecastToCSV(forecastUser || {}, { datesHint })],
      {
        type: "text/csv;charset=utf-8",
      },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    const key = tagsActive
      ? `tags_${selectedTags.join("-")}`
      : `${lease || "lease"}`;

    a.download = `${init.companyLabel
      .toLowerCase()
      .replace(/\s+/g, "_")}_user_forecast_p50_${key}_${iso(new Date())}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleExportJSON() {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    const key = tagsActive
      ? `tags_${selectedTags.join("-")}`
      : `${lease || "lease"}`;

    a.download = `${init.companyLabel
      .toLowerCase()
      .replace(/\s+/g, "_")}_forecast_${key}_${iso(new Date())}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleExportEconCSV() {
    const res = econBundle?.selectedResult;
    if (!res) return;
    const blob = new Blob([toEconCSV(res)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    const key = tagsActive
      ? `tags_${selectedTags.join("-")}`
      : `${lease || "lease"}`;

    a.download = `${init.companyLabel
      .toLowerCase()
      .replace(
        /\s+/g,
        "_",
      )}_econ_${econBundle?.selectedKey || "Base"}_${key}_${iso(
      new Date(),
    )}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function toggleStream(s) {
    setStreams((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  }

  function resetZoom() {
    const inst = chartRef.current?.getEchartsInstance?.();
    inst?.dispatchAction({ type: "dataZoom", start: 0, end: 100 });
  }

  async function copyLink() {
    try {
      // Copy the *staged overlay API URL*
      // - In tags mode, do NOT include lease
      // - Force aggregate=1 for dashboard-compatible shape
      const apiParams = {
        horizon,
        streams: streams.join(","),
        limit: 250,
        ...(init.from ? { from: init.from } : {}),
        ...(init.thru ? { thru: init.thru } : {}),
      };

      if (tagsActive) {
        apiParams.tags = selectedTags.join(",");
        apiParams.tags_mode = tagsMode || "any";
        apiParams.aggregate = 1;
        if (includeInactive) apiParams.include_inactive = 1;
      } else {
        apiParams.lease = lease;
      }

      const apiUrl = buildOverlayUrl(apiParams);
      await navigator.clipboard.writeText(apiUrl);
    } catch {}
  }

  function onTagsChange(e) {
    const values = Array.from(e.target.selectedOptions)
      .map((o) => String(o.value || "").trim())
      .filter(Boolean);

    // option.value MUST be TagID
    setSelectedTags(values);
  }

  const matchedLeaseCount = data?.meta?.tags?.matched_leases;

  const subtitle = tagsActive
    ? `Tags (TagID): ${selectedTags
        .map((id) => tagDescById.get(id) || id)
        .join(", ")}${
        definedNum(matchedLeaseCount) ||
        Number.isFinite(Number(matchedLeaseCount))
          ? ` • Leases: ${String(matchedLeaseCount)}`
          : ""
      } • Mode: aggregate`
    : `Lease: ${
        !lease
          ? "—"
          : lease === "~ALL~"
            ? "~ALL~"
            : leaseNameById.get(lease) || lease
      }`;

  const econSelected = econBundle?.selectedResult || null;
  const econDecisionColor =
    econSelected?.decision === "Shut-in candidate"
      ? "#ef4444"
      : econSelected?.decision === "Monitor"
        ? "#f59e0b"
        : undefined;

  const effectiveWI = computeEffectiveWI(econ);
  const derivedNRI = (1 - clamp01(econ.royalty)) * effectiveWI;

  // Expose internal state to wrapper UIs via context (must be before early return)
  const _toggleStream = (s) =>
    setStreams((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  const _ctxValue = useMemo(() => ({
    econBundle, econSelected, cards, data, loading, error,
    lease, leaseOptions, leaseNameById,
    selectedTags, tagsMode, tagOptions, tagDescById, tagsActive,
    dark, streams, logScale, showBands, showMA, forecastMode,
    econ, init, latencyMs, runAt, subtitle,
    econDecisionColor, effectiveWI, derivedNRI,
    useUserForecast, hasUserForecast, horizon, cumulativeMode,
    setEconModalOpen, setEconTab, setDark, load,
    setLease, setHorizon, setStreams, toggleStream: _toggleStream,
    setShowBands, setShowMA, setLogScale, setShowOutliers,
    setUseUserForecast, setCumulativeMode, setZeroAsNull,
    showOutliers, zeroAsNull,
    openUserForecastEditor, openChartPrefsModal, resetZoom,
    handleExportCSV, handleExportPNG, handleExportJSON, copyLink,
    chartRef,
  }), [
    econBundle, econSelected, cards, data, loading, error,
    lease, leaseOptions, leaseNameById,
    selectedTags, tagsMode, tagOptions, tagDescById, tagsActive,
    dark, streams, logScale, showBands, showMA, forecastMode,
    econ, init, latencyMs, runAt, subtitle,
    econDecisionColor, effectiveWI, derivedNRI,
    useUserForecast, hasUserForecast, horizon, cumulativeMode,
    showOutliers, zeroAsNull,
  ]);

  // ADMIN GATE (render-time, AFTER hooks)
  if (!init.isAdmin) return (
    <ForecastDashboardContext.Provider value={_ctxValue}>
      {null}
    </ForecastDashboardContext.Provider>
  );

  // ---------- Econ tooltips (UI) ----------
  const ECON_TIPS = {
    // Section-level (headers)
    sections: {
      decision: `Decision logic (what this panel means)
• We build a DAILY cashflow series using actual + forecast(p50) volumes.
• Daily CF = OwnerRev − Opex − CapexOwner.
• "First negative CF" = first day CF < 0 in the econ window.
• "Economic limit" = earliest trigger date from:
  - Rolling-average rule (avg CF over N days < 0), OR
  - Consecutive-negative rule (N days CF < 0).
• Labels:
  - Operate: no near-term triggers.
  - Monitor: first negative CF soon, but no econ-limit trigger yet.
  - Shut-in candidate: econ-limit trigger hits within the window.`,
      kpis: `KPIs (computed over the econ window)
• PV: sum of discounted daily cashflows (discounted from window start using the annual discount rate).
  - If discountRateAnnual = 0.10, this is effectively PV10 over the window.
• Cumulative CF: sum of UNDISCOUNTED daily cashflows over the window.
• Netback ($/boe): totalCash / totalBOE (owner-level net cash per BOE).
  - Includes royalties, taxes, LOE, and capex timing.
• LOE ($/boe): totalOpex / totalBOE (owner-level operating cost per BOE).
• Payout date: first day cumulative CF ≥ 0 (most meaningful when you include upfront capex).`,
      breakevens: `Breakevens & sensitivities (what these mean)
• These solve for a single input value while holding everything else constant.
• "Breakeven Oil for PV=0":
  - Finds the oil price where PV over the window equals zero.
• "Breakeven Oil for Min CF ≥ 0":
  - Finds the oil price where the WORST daily CF in the window is ≥ 0 (very conservative).
• "Max Fixed LOE for PV ≥ 0":
  - Finds the highest monthly fixed LOE that still yields PV ≥ 0.`,
      prices: `Prices & volume adjustments
• Oil/Gas price are the benchmark prices before deductions.
• Net price used in revenue:
  - Oil net = OilPrice − OilDiff − OilMkt − OilTrans
  - Gas net = GasPrice − GasDiff − GasMkt − GasTrans
• Gas shrink reduces sellable gas: salesGas = producedGas × (1 − shrink).
• Downtime reduces produced volumes: uptime = 1 − downtimePct; volumes × uptime.`,
      marketing: `Marketing / transport
• Per-unit deductions from price (gathering, treating, marketing, fuel, trucking, pipeline tariffs, etc.).
• These reduce realized price directly (not volumes).`,
      ownership: `Ownership & taxes
• Royalty is taken off gross revenue first (before WI).
• WI (working interest) is the fraction of post-royalty revenue AND costs you own.
• Optional NRI override:
  - If set (>0), treat it as NRI and derive WI ≈ NRI / (1 − royalty).
• Severance taxes are applied on gross stream revenue (oil & gas separately).
• Ad valorem is modeled as a % of gross revenue.`,
      opex: `Opex (LOE) model
• Fixed LOE/month: daily fixed cost bucket (labor, routine ops, etc.).
• Var LOE/boe: per-BOE lifting cost.
• Water disposal: $/bbl water (often dominant late-life).
• Base maint/month: baseline maintenance.
• Low-rate extra maint: adds extra daily maintenance when BOE/day falls below a threshold.
• Chemicals/electricity: per-BOE variable consumables.
• Overhead/month: admin/field overhead.
• Owner Opex = (all opex components) × WI.`,
      capex: `Capex / discount / rules
• Capex upfront/date: applied as a one-day cost on the capex date (owner share × WI).
• Abandonment cost/date: applied as a one-day cost on the abandonment date (owner share × WI).
• Discount rate annual: PV discounts daily CF from the window start:
  PV = Σ CF_day × 1/(1+r)^(days/365)
• Econ window days: forward-looking horizon from Thru used for econ evaluation.
• Rolling avg days: economic limit when rolling avg CF < 0.
• Consecutive neg days: economic limit when CF < 0 for N consecutive days.
• Economic limit date is the EARLIEST of those triggers.`,
    },

    // Summary row labels
    summary: {
      firstNegDate: `First day in the econ window where daily owner-level cashflow < 0.
Owner CF already includes: royalty, taxes, WI share of LOE, and capex timing.`,
      econLimitDate: `Economic limit date = earliest trigger date among:
• Rolling-average cashflow < 0 (over N days), OR
• N consecutive negative cashflow days.`,
      rollingNegDate: `Rolling-average trigger:
We compute avg(daily CF) over the last N days.
If that average drops below 0, the well is considered uneconomic by this rule.`,
      consecutiveNegDate: `Consecutive-negative trigger:
If daily CF is negative for N straight days, we flag economic limit.`,
      pv: `PV = sum of discounted daily cashflows in the econ window.
Discounting is from the window start date using the annual discount rate.`,
      totalCash: `Cumulative CF = sum of undiscounted daily cashflows over the window.`,
      totalNgl: `Total NGL barrels produced over the econ window (derived from gas × NGL yield).`,
      totalGrossNgl: `Total gross NGL revenue over the econ window (NGL bbl × NGL price per bbl).`,
      netback: `Netback ($/boe) = totalCash / totalBOE.`,
      loePerBoe: `LOE ($/boe) = totalOpex / totalBOE.`,
      payoutDate: `First date cumulative cashflow ≥ 0 (within the window).`,
    },

    // Breakeven row labels
    breakevens: {
      beOilPV0: `Oil price that makes PV over the selected econ window equal to 0.
Solved via bisection while keeping all other assumptions fixed.`,
      beOilMinCF0: `Oil price that makes the MINIMUM daily cashflow in the window ≥ 0.
This is conservative because it requires every day to be non-negative.`,
      maxFixedLoePV0: `Maximum monthly fixed LOE that still yields PV ≥ 0 over the window.
Useful for “how much fixed overhead can this tolerate?” sensitivity.`,
    },

    // Assumptions field labels
    assumptions: {
      oilPrice: `Benchmark oil price used for revenue before deductions (diff/marketing/transport).
Realized (net) oil price = OilPrice − OilDiff − OilMkt − OilTrans.`,
      gasPrice: `Benchmark gas price used for revenue before deductions (diff/marketing/transport).
Realized (net) gas price = GasPrice − GasDiff − GasMkt − GasTrans.`,

      nglYieldBblPerMcf: `NGL yield in bbl per produced mcf of gas.
NGL bbl/day = producedGasMcf × nglYieldBblPerMcf (then included in BOE and revenue).`,
      nglMode: `NGL pricing method:

• % of WTI:
  NGL price per bbl = Oil $/bbl × NGL % of WTI
  "WTI" here uses the Oil $/bbl value entered above in this panel.

• $ / gallon:
  NGL price per bbl = (Entered $/gal × 42)

Example:
If Oil = $70 and NGL % = 0.45,
NGL price = 70 × 0.45 = $31.50 per bbl.`,
      oilDiff: `Oil differential/basis penalty ($/bbl).
Subtracts from benchmark to represent quality, location, gravity, basis, etc.`,
      gasDiff: `Gas differential/basis penalty ($/mcf).
Subtracts from benchmark to represent basis, BTU, gathering penalties, etc.`,
      gasShrink: `Fraction of produced gas that is NOT sold (fuel/plant shrink/measurement).
Sales gas = produced gas × (1 − shrink).`,
      downtimePct: `Fraction of time the well is down.
We apply uptime = 1 − downtimePct and multiply volumes by uptime.`,

      nglYieldBblPerMcf: `NGL yield used to derive NGL barrels from produced gas.
NGL bbl = produced gas (mcf) × NGL yield (bbl/mcf).`,
      nglMode: `How NGL price is set:
• % of WTI: NGL $/bbl = WTI × (% of WTI)
• $/gal: NGL $/bbl = ($/gal) × 42`,
      nglPricePerGal: `NGL price in $/gallon (only used when price mode = $/gallon).
Converted to $/bbl by multiplying by 42.`,
      nglPctOfWti: `Fraction of Oil $/bbl used to price NGL.

Example:
Oil = $70
NGL % = 0.45
→ NGL = $31.50 per bbl

WTI reference comes directly from the Oil $/bbl input above.`,
      oilMarketingPerBbl: `Per-bbl marketing/treating/gathering fees for oil.
Directly reduces realized oil price.`,
      gasMarketingPerMcf: `Per-mcf marketing/treating/gathering fees for gas.
Directly reduces realized gas price.`,
      oilTransportPerBbl: `Per-bbl transport cost for oil (trucking/pipeline tariffs).
Directly reduces realized oil price.`,
      gasTransportPerMcf: `Per-mcf transport cost for gas (pipeline tariffs, compression fees, etc.).
Directly reduces realized gas price.`,

      royalty: `Royalty rate (0–1). Paid on gross revenue BEFORE WI.
Owner revenue is computed after royalty and taxes, then multiplied by WI.`,
      workingInterest: `Working interest (0–1). Your share of:
• post-royalty revenue, and
• operating/capex costs.`,
      nriOverride: `Optional NRI (0–1). If set (>0), we treat it as NRI and derive WI:
WI ≈ NRI / (1 − royalty). Useful when you know NRI but not WI explicitly.`,
      oilSevTax: `Oil severance tax rate (0–1) applied to gross oil revenue.`,
      gasSevTax: `Gas severance tax rate (0–1) applied to gross gas revenue.`,
      adValoremTax: `Ad valorem tax rate (0–1) applied to total gross revenue.`,

      fixedLoePerMonth: `Fixed operating cost per month (labor, routine ops, fixed contracts).
Converted to a daily cost bucket: fixed/30.`,
      varLoePerBoe: `Variable operating cost per BOE (lifting costs that scale with rate).`,
      waterDisposalPerBbl: `Cost per bbl of produced water (SWD fees, hauling, etc.).`,
      chemicalsPerBoe: `Chemicals/consumables cost per BOE.`,
      electricityPerBoe: `Electric power cost per BOE (pumping, compression, etc.).`,
      overheadPerMonth: `Monthly overhead allocated to the well (admin/field overhead).
Converted to daily: overhead/30.`,
      baseMaintPerMonth: `Baseline maintenance per month (routine repairs).
Converted to daily: baseMaint/30.`,
      lowRateBoePerDayThreshold: `If BOE/day is below this threshold, we add the low-rate extra maintenance daily.`,
      lowRateExtraMaintPerMonth: `Extra maintenance per month applied only when BOE/day < threshold.
Converted to daily: lowRateExtra/30.`,

      capexUpfront: `One-time capital cost applied on the capex date (e.g., workover, recompletion).
Owner share = capex × WI.`,
      capexDate: `Date to apply the upfront capex (YYYY-MM-DD).
If blank, defaults to the econ window start date.`,
      abandonmentCost: `One-time abandonment / P&A cost applied on the abandonment date.
Owner share = abandonment × WI.`,
      abandonmentDate: `Date to apply abandonment cost (YYYY-MM-DD).
If blank, defaults to the last day in the econ window.`,
      discountRateAnnual: `Annual discount rate used for PV.
PV discounts each day’s cashflow from window start by (1+r)^(days/365).`,
      unprofitableSoonDays: `How far forward (days) we evaluate econ starting from Thru.
This is the horizon used for PV, decision logic, and triggers.`,
      econLimitRollingDays: `Window size N for rolling-average economic limit rule.
If avg(CF) over last N days < 0 → rolling trigger date.`,
      econLimitConsecutiveNegDays: `N for consecutive-negative economic limit rule.
If CF < 0 for N consecutive days → consecutive trigger date.`,
    },
  };

  function InfoTip({ text, align = "left" }) {
    if (!text) return null;
    return (
      <span
        className={`tipWrap ${align === "right" ? "right" : ""}`}
        tabIndex={0}
        role="button"
        aria-label="Info"
      >
        <span className="tipIcon" aria-hidden="true">
          i
        </span>
        <span className="tipBubble" role="tooltip">
          {text}
        </span>
      </span>
    );
  }

  return (
    <ForecastDashboardContext.Provider value={_ctxValue}>
    <div className="forecast-root" data-theme={dark ? "dark" : "light"}>
      <style>{`
        :root {
          --bg: #ffffff; --panel: #f8fafc; --text: #111827; --muted: #6b7280;
          --ring: #3b82f6; --border: #e5e7eb; --chip: #eef2ff;
          --brand: #111827;
          --shadow: 0 10px 30px rgba(0,0,0,0.08);
        }
        [data-theme="dark"] {
          --bg: #0b1220; --panel: #0f172a; --text: #e5e7eb; --muted: #9ca3af;
          --ring: #60a5fa; --border: #1f2937; --chip: #111827;
          --brand: #e5e7eb;
          --shadow: 0 12px 40px rgba(0,0,0,0.35);
        }
        .forecast-root { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial; background: var(--bg); color: var(--text); min-height: 100vh; }
        .topbar { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid var(--border); position: sticky; top:0; background: var(--bg); z-index: 40;}
        .brand { display:flex; align-items:center; gap:10px; }
        .logo { width:20px; height:20px; border-radius:5px; background: linear-gradient(135deg, #2563eb 0%, #10b981 100%); }
        .title { font-weight: 800; font-size: 14px; color: var(--brand); }
        .subtitle { color: var(--muted); font-size: 12px; }
        .layout { display:grid; grid-template-columns: 340px 1fr; gap: 0; }
        @media (max-width: 980px){ .layout { grid-template-columns: 1fr; } }
        .panel { background: var(--panel); border-right: 1px solid var(--border); padding: 12px; }
        .panel h3 { margin: 8px 0 6px; font-size: 12px; text-transform: uppercase; color: var(--muted); letter-spacing:.08em; }
        .row { display:flex; gap:8px; align-items:center; margin: 8px 0; flex-wrap: wrap; }
        .row label { font-size: 12px; color: var(--muted); }
        .row input[type="date"], .row input[type="number"], .row input[type="text"], .row select {
          background: var(--bg); border:1px solid var(--border); color: var(--text); border-radius:8px; padding:6px 8px;
        }
        .row input[disabled] { opacity: 0.75; cursor: not-allowed; }

        .row select[multiple] { min-height: 96px; }
        .chips { display:flex; gap:8px; flex-wrap:wrap; }
        .chip { border:1px solid var(--border); background: var(--chip); padding:6px 10px; border-radius:999px; cursor:pointer; font-weight:600; font-size:12px; }
        .chip.on { outline: 2px solid var(--ring); }
        .actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
        .btn { background: var(--bg); border:1px solid var(--border); border-radius:10px; padding:8px 12px; cursor:pointer; display:inline-flex; gap:8px; align-items:center; }
        .btn.primary { background:#3b82f6; border-color:#2563eb; color:white; }
        .btn.warn { background: rgba(245,158,11,0.15); border-color: rgba(245,158,11,0.45); }
        .btn.danger { background: rgba(239,68,68,0.14); border-color: rgba(239,68,68,0.45); }
        .btn:disabled { opacity:.6; cursor:default; }
        .pill { font-size: 11px; padding: 2px 8px; border-radius: 999px; border:1px solid var(--border); color: var(--muted); }
        .pill.good { border-color: rgba(16,185,129,0.45); color: ${dark ? "#34d399" : "#059669"}; }
        .pill.warn { border-color: rgba(245,158,11,0.55); color: ${dark ? "#fbbf24" : "#b45309"}; }
        .pill.bad { border-color: rgba(239,68,68,0.55); color: ${dark ? "#f87171" : "#b91c1c"}; }
        .content { padding: 8px 12px; }
        .cards { display:grid; grid-template-columns: repeat(3, minmax(220px, 1fr)); gap: 8px; margin: 8px 0 12px; }
        @media (max-width: 980px){ .cards{ grid-template-columns: 1fr; } }
        .card { background: var(--panel); border:1px solid var(--border); border-radius: 14px; padding: 12px; box-shadow: none; }
        .card h4 { margin: 0 0 4px; font-size: 12px; text-transform: uppercase; letter-spacing:.08em; color: var(--muted); }
        .card .val { font-weight: 800; font-size: 18px; }
        .card .sub { color: var(--muted); font-size: 12px; }
        .footnote { color: var(--muted); font-size: 12px; margin-top: 8px; }
        .err { color: #ef4444; }
        .skeleton { height: 520px; border:1px dashed var(--border); border-radius: 12px; display:flex; align-items:center; justify-content:center; color: var(--muted); }
        .econBox { background: var(--bg); border:1px solid var(--border); border-radius: 12px; padding: 10px; margin-top: 10px; }
        .econGrid { display:grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .econKpi { font-size: 12px; color: var(--muted); }
        .econKpi b { color: var(--text); font-size: 13px; }
        .kpiStrip { display:flex; gap:10px; flex-wrap:wrap; margin: 6px 0 10px; }
        .kpiChip { background: var(--panel); border:1px solid var(--border); border-radius: 999px; padding: 6px 10px; font-size: 12px; color: var(--muted); display:flex; gap:6px; align-items:center; }
        .kpiChip b { color: var(--text); }
        .divider { height: 1px; background: var(--border); margin: 10px 0; opacity: 0.8; }

        /* Snapshot stamp (side panel) */
        .stampBox {
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 14px;
          padding: 10px 12px;
          margin: 8px 0 10px;
          box-shadow: none;
        }
        .stampHeader {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 10px;
        }
        .stampLabel {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: .08em;
          color: var(--muted);
          font-weight: 800;
          line-height: 1.2;
        }
        .stampValue {
          margin-top: 4px;
          font-size: 16px;
          font-weight: 950;
          color: var(--text);
          line-height: 1.15;
          font-variant-numeric: tabular-nums;
          word-break: break-word;
        }
        .stampSubRow {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-top: 8px;
        }

        /* Tooltips */
        .h5Row { display:flex; align-items:center; gap:8px; }
        .kpiLabel { display:inline-flex; align-items:center; gap:6px; }
        .econKpiLabel { display:flex; align-items:center; gap:6px; }
        .tipWrap {
          position: relative;
          display: inline-flex;
          align-items: center;
          cursor: help;
          outline: none;
        }
        .tipIcon {
          width: 16px;
          height: 16px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 900;
          color: var(--text);
          border: 1px solid var(--border);
          background: var(--bg);
          opacity: 0.85;
          user-select: none;
          line-height: 1;
        }
        .tipWrap:hover .tipIcon { opacity: 1; }
        .tipWrap:focus-visible .tipIcon {
          outline: 2px solid var(--ring);
          outline-offset: 2px;
          opacity: 1;
        }
        .tipBubble {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          width: min(520px, 86vw);
          max-height: min(60vh, 360px);
          overflow: auto;
          overscroll-behavior: contain;
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 14px;
          box-shadow: var(--shadow);
          padding: 10px 12px;
          font-size: 12px;
          line-height: 1.35;
          color: var(--text);
          white-space: pre-wrap;
          word-break: break-word;
          z-index: 9999;
          opacity: 0;
          transform: translateY(4px);
          pointer-events: none;
          transition: opacity .14s ease, transform .14s ease;
        }
        .tipWrap.right .tipBubble { left: auto; right: 0; }
        .tipWrap:hover .tipBubble,
        .tipWrap:focus .tipBubble,
        .tipWrap:focus-within .tipBubble {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }
        .tipBubble::before {
          content: "";
          position: absolute;
          top: -6px;
          left: 12px;
          width: 10px;
          height: 10px;
          background: var(--bg);
          border-left: 1px solid var(--border);
          border-top: 1px solid var(--border);
          transform: rotate(45deg);
        }
        .tipWrap.right .tipBubble::before { left: auto; right: 14px; }

        @media (prefers-reduced-motion: reduce){
          .tipBubble { transition: none; }
        }

        /* Modal */
        .modalOverlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.45);
          padding: 14px;
          z-index: 80;
          overflow: auto;
          -webkit-overflow-scrolling: touch;
        }
        .modalCard {
          width: min(980px, 100%);
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 16px;
          box-shadow: var(--shadow);
          display:flex; flex-direction: column;
          margin: 0 auto;
          overflow: visible; /* IMPORTANT: don't clip tooltips */
        }
        .modalHeader {
          display:flex; align-items:center; justify-content:space-between;
          padding: 12px 14px;
          border-bottom: 1px solid var(--border);
          background: var(--bg);
          position: sticky;
          top: 0;
          z-index: 2;
          border-top-left-radius: 16px;
          border-top-right-radius: 16px;
        }
        .modalTitle { font-weight: 900; font-size: 14px; color: var(--brand); }
        .modalSub { font-size: 12px; color: var(--muted); margin-top: 2px; }
        .modalBody { padding: 12px 14px; overflow: visible; } /* IMPORTANT: don't clip tooltips */
        .modalFooter {
          padding: 10px 14px;
          border-top: 1px solid var(--border);
          background: var(--bg);
          display:flex; justify-content:space-between; gap: 10px; flex-wrap:wrap;
          position: sticky;
          bottom: 0;
          z-index: 2;
          border-bottom-left-radius: 16px;
          border-bottom-right-radius: 16px;
        }
        .tabs { display:flex; gap:8px; flex-wrap: wrap; margin-bottom: 10px; }
        .tab { padding: 6px 10px; border-radius: 999px; border:1px solid var(--border); background: var(--bg); cursor: pointer; font-size: 12px; }
        .tab.on { outline: 2px solid var(--ring); font-weight: 800; }
        .grid2 { display:grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        @media (max-width: 900px){ .grid2{ grid-template-columns: 1fr; } }
        .panelCard { background: var(--panel); border: 1px solid var(--border); border-radius: 14px; padding: 12px; }
        .panelCard h5 { margin: 0 0 8px; font-size: 12px; text-transform: uppercase; letter-spacing: .08em; color: var(--muted); }
        .kpiBig { font-weight: 900; font-size: 20px; }
        .kpiRow { display:flex; justify-content:space-between; gap: 10px; font-size: 12px; color: var(--muted); margin-top: 6px; }
        .kpiRow b { color: var(--text); }
        table.tbl { width: 100%; border-collapse: collapse; }
        .tbl th, .tbl td { border-bottom: 1px solid var(--border); padding: 8px 6px; font-size: 12px; text-align: left; vertical-align: top; }
        .tbl th { color: var(--muted); font-weight: 800; text-transform: uppercase; letter-spacing:.06em; font-size: 11px; }
        .tbl td.num { text-align: right; font-variant-numeric: tabular-nums; }

        /* ---- Chart Prefs modal ---- */
        .prefsShell { position: relative; }
        .prefsTabs { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:12px; }
        .prefsSection { border:1px solid var(--border); border-radius:14px; padding:12px; background: var(--panel); margin-bottom:12px; }
        .prefsHeading { font-weight: 900; margin-bottom: 10px; }
        .prefsRow { display:flex; gap:12px; align-items:flex-end; flex-wrap:wrap; }
        .prefsLabel { min-width: 180px; font-weight: 900; }
        .prefsControl { display:flex; flex-direction:column; gap:6px; min-width: 150px; }
        .prefsSeriesCard { border:1px solid var(--border); border-radius:14px; padding:12px; background: rgba(0,0,0,0.02); }
        [data-theme="dark"] .prefsSeriesCard { background: rgba(255,255,255,0.03); }

        .swatch { display:flex; align-items:center; gap:10px; border:1px solid var(--border); border-radius:12px; padding:8px 10px; background: var(--bg); cursor:pointer; }
        .swatchDot { width:18px; height:18px; border-radius:999px; border:1px solid var(--border); display:inline-block; }
        .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; font-size: 12px; }

        .pickerPop {
          position: fixed;
          right: 22px;
          top: 90px;
          z-index: 1000;
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 12px;
          box-shadow: var(--shadow);
        }

        .warnBox {
          margin-top: 10px;
          padding: 10px;
          border-radius: 12px;
          border: 1px solid rgba(239,68,68,0.35);
          background: rgba(239,68,68,0.08);
          color: var(--text);
        }
      `}</style>
      <div className="topbar">
        <div className="brand">
          <div className="logo" />
          <div>
            <div className="title">
              {init.companyLabel} • Production Forecasts
            </div>
            <div className="subtitle">{subtitle}</div>
          </div>
        </div>

        <div className="actions">
          {econSelected?.decision ? (
            <span
              className={`pill ${
                econSelected.decision === "Operate"
                  ? "good"
                  : econSelected.decision === "Monitor"
                    ? "warn"
                    : "bad"
              }`}
              title={econSelected.decisionDetail || ""}
            >
              Econ: {econSelected.decision}
            </span>
          ) : (
            <span className="pill">Econ: —</span>
          )}

          <button
            className={`btn ${
              econSelected?.decision === "Shut-in candidate"
                ? "danger"
                : econSelected?.decision === "Monitor"
                  ? "warn"
                  : ""
            }`}
            onClick={() => {
              setEconModalOpen(true);
              setEconTab("summary");
            }}
            disabled={!data}
            title="Open decision metrics & econ layer"
          >
            Econ & Decisions
            {econSelected?.econLimitDate ? (
              <span style={{ color: econDecisionColor, fontWeight: 900 }}>
                • {econSelected.econLimitDate}
              </span>
            ) : null}
          </button>

          <button className="btn" onClick={() => setDark((d) => !d)}>
            {dark ? "Light" : "Dark"} mode
          </button>

          <button className="btn" onClick={resetZoom}>
            Reset zoom
          </button>

          <button
            className={`btn ${useUserForecast ? "primary" : ""}`}
            onClick={() => setUseUserForecast((v) => !v)}
            disabled={!data}
            title="Toggle between base DCA P50 and user-adjusted P50"
          >
            Forecast: {useUserForecast ? "User" : "Base"}
            {hasUserForecast ? <span className="chip">saved</span> : null}
          </button>

          <button
            className="btn"
            onClick={openUserForecastEditor}
            disabled={!data}
            title="Edit user-adjusted P50 (stored locally per lease/tags)"
          >
            Edit P50
          </button>

          <button
            className={`btn ${showBands ? "warn" : ""}`}
            onClick={() => setShowBands((v) => !v)}
            disabled={!data}
            title="Toggle p10–p90 bands (disabled by default)"
          >
            Bands: {showBands ? "On" : "Off"}
          </button>

          <button
            className="btn"
            onClick={openChartPrefsModal}
            disabled={!init.userIdRaw}
            title="Edit per-series render (bar/line/scatter), stroke styles, and colors (saved to users.ChartsPref)"
          >
            Styles
          </button>

          <button className="btn" onClick={copyLink}>
            Copy link
          </button>
          <button
            className="btn"
            onClick={openChartPrefsModal}
            disabled={!init.userIdRaw}
            title="Edit per-series render (bar/line/scatter), stroke styles, and colors (saved to users.ChartsPref)"
          >
            Styles
          </button>
          <button className="btn" onClick={handleExportCSV} disabled={!data}>
            Export CSV
          </button>

          <button className="btn" onClick={handleExportPNG} disabled={!data}>
            Export PNG
          </button>

          <button className="btn" onClick={handleExportJSON} disabled={!data}>
            Export JSON
          </button>
        </div>
      </div>

      <Modal
        open={userForecastOpen}
        title={`User Forecast (P50) • ${useUserForecast ? "Active" : "Inactive"}`}
        onClose={() => {
          setUserForecastOpen(false);
          setUserForecastDraft(null);
        }}
        footer={
          <div
            className="row"
            style={{ gap: 8, justifyContent: "space-between" }}
          >
            <div className="row" style={{ gap: 8 }}>
              <button
                className="btn"
                onClick={handleExportUserForecastCSV}
                disabled={!data}
              >
                Download CSV
              </button>

              <button
                className="btn"
                onClick={() => ufFileRef.current?.click?.()}
                disabled={!data}
                title="Import CSV with columns: date,Oil,Gas,Water"
              >
                Import CSV
              </button>

              <input
                ref={ufFileRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  handleImportUserForecastFile(f);
                  e.target.value = "";
                }}
              />
            </div>

            <div className="row" style={{ gap: 8 }}>
              <button
                className="btn danger"
                onClick={() => {
                  clearUserForecast();
                  setUserForecastDraft({
                    scale: { Oil: 1, Gas: 1, Water: 1 },
                    overrides: { Oil: {}, Gas: {}, Water: {} },
                    editDays: 90,
                  });
                }}
                disabled={!data}
                title="Remove saved user forecast for this lease/tags"
              >
                Clear
              </button>

              <button
                className="btn"
                onClick={() => {
                  setUserForecastOpen(false);
                  setUserForecastDraft(null);
                }}
              >
                Cancel
              </button>

              <button
                className="btn primary"
                onClick={saveUserForecastDraft}
                disabled={!data || !userForecastDraft}
              >
                Save
              </button>
            </div>
          </div>
        }
      >
        <div className="muted" style={{ marginBottom: 10 }}>
          This edits only the <b>P50</b> forecast. Default behavior is to scale
          the base <b>DCA P50</b> (so DCA stays intact). Optional per-date
          overrides take precedence over the scaled base.
        </div>

        <div className="card" style={{ marginBottom: 12 }}>
          <div
            className="row"
            style={{ gap: 10, alignItems: "center", flexWrap: "wrap" }}
          >
            <div style={{ fontWeight: 700 }}>Scale (multiplier)</div>

            {STREAMS.map((s) => (
              <label
                key={s}
                className="row"
                style={{ gap: 6, alignItems: "center" }}
              >
                <span style={{ width: 54 }}>{s}</span>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  value={userForecastDraft?.scale?.[s] ?? 1}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setUserForecastDraft((prev) => {
                      const next = prev
                        ? JSON.parse(JSON.stringify(prev))
                        : JSON.parse(JSON.stringify(userForecastEffective));
                      next.scale = next.scale || { Oil: 1, Gas: 1, Water: 1 };
                      next.scale[s] = raw === "" ? "" : Number(raw);
                      return next;
                    });
                  }}
                />
              </label>
            ))}

            <label
              className="row"
              style={{ gap: 6, alignItems: "center", marginLeft: 8 }}
              title="How many forecast days to show in the override table"
            >
              <span>Override rows</span>
              <input
                className="input"
                type="number"
                min={7}
                max={365}
                value={userForecastDraft?.editDays ?? 90}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setUserForecastDraft((prev) => {
                    const next = prev
                      ? JSON.parse(JSON.stringify(prev))
                      : JSON.parse(JSON.stringify(userForecastEffective));
                    next.editDays = v;
                    return next;
                  });
                }}
              />
              <span className="muted">days</span>
            </label>
          </div>
        </div>

        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            Per-date overrides (advanced)
          </div>

          <div className="muted" style={{ marginBottom: 8 }}>
            Leave blank to use scaled base. Enter a number to override that
            date’s P50.
          </div>

          <div style={{ maxHeight: 420, overflow: "auto" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Date</th>
                  {STREAMS.map((s) => (
                    <th key={s}>{s} override</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(ufEditDates || []).map((d) => (
                  <tr key={d}>
                    <td style={{ whiteSpace: "nowrap" }}>{d}</td>

                    {STREAMS.map((s) => {
                      const base = baseForecastMidMaps?.[s]?.get?.(d);
                      const sc = Number(userForecastDraft?.scale?.[s] ?? 1);
                      const ph = definedNum(base)
                        ? fmt0.format(base * (definedNum(sc) ? sc : 1))
                        : "";
                      const val = userForecastDraft?.overrides?.[s]?.[d] ?? "";

                      return (
                        <td key={s}>
                          <input
                            className="input"
                            value={val}
                            placeholder={ph}
                            onChange={(e) => {
                              const raw = e.target.value;
                              setUserForecastDraft((prev) => {
                                const next = prev
                                  ? JSON.parse(JSON.stringify(prev))
                                  : JSON.parse(
                                      JSON.stringify(userForecastEffective),
                                    );
                                next.overrides = next.overrides || {
                                  Oil: {},
                                  Gas: {},
                                  Water: {},
                                };
                                next.overrides[s] = next.overrides[s] || {};
                                if (raw === "") {
                                  delete next.overrides[s][d];
                                } else {
                                  const n = Number(raw);
                                  next.overrides[s][d] = definedNum(n)
                                    ? n
                                    : raw;
                                }
                                return next;
                              });
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
      <Modal
        open={chartPrefsOpen}
        title="Chart Styles"
        onClose={closeChartPrefsModal}
        footer={
          <div
            className="row"
            style={{ justifyContent: "space-between", gap: 10 }}
          >
            <div className="row" style={{ gap: 10 }}>
              <button
                className="btn"
                onClick={resetChartPrefsDraft}
                disabled={!chartPrefsDraft}
              >
                Reset to defaults
              </button>
              <span className="muted" style={{ fontSize: 12 }}>
                Saved to users.ChartsPref (forecastDash)
              </span>
            </div>
            <div className="row" style={{ gap: 10 }}>
              <button className="btn" onClick={closeChartPrefsModal}>
                Cancel
              </button>
              <button
                className={`btn primary`}
                onClick={saveChartPrefsDraft}
                disabled={!chartPrefsDraft || chartsPrefSaving}
              >
                {chartsPrefSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        }
      >
        {!chartPrefsDraft ? (
          <div className="muted">Loading…</div>
        ) : (
          <div className="prefsShell">
            <div className="prefsTabs">
              {STREAMS.map((s) => (
                <button
                  key={s}
                  className={`btn ${chartPrefsTab === s ? "primary" : ""}`}
                  onClick={() => setChartPrefsTab(s)}
                  type="button"
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="prefsSection">
              <div className="prefsHeading">Base stream color</div>
              <div className="prefsRow">
                <div className="prefsLabel">Color</div>
                <button
                  type="button"
                  className="swatch"
                  onClick={() =>
                    setChartPrefsPickerKey(`base:${chartPrefsTab}`)
                  }
                  title="Pick base stream color"
                >
                  <span
                    className="swatchDot"
                    style={{
                      background:
                        chartPrefsDraft.colors?.[chartPrefsTab] ||
                        COLORS[chartPrefsTab],
                    }}
                  />
                  <span className="mono">
                    {chartPrefsDraft.colors?.[chartPrefsTab] ||
                      COLORS[chartPrefsTab]}
                  </span>
                </button>
              </div>
            </div>

            <div className="prefsSection">
              <div className="prefsHeading">Per-series styles</div>

              {FD_SERIES_KINDS.map(({ key, label }) => {
                const s = chartPrefsTab;
                const pref = chartPrefsDraft.seriesPrefs?.[s]?.[key] || {};
                const render =
                  pref.render ||
                  (key === "Actual" || key === "Outliers" || key === "Anomalies"
                    ? "scatter"
                    : "line");
                const colorVal = pref.color || "";

                return (
                  <div key={`${s}:${key}`} className="prefsSeriesCard">
                    <div className="prefsRow">
                      <div className="prefsLabel">{label}</div>

                      <div className="prefsControl">
                        <div className="muted">Render</div>
                        <select
                          className="inp"
                          value={render}
                          onChange={(e) =>
                            updateChartPrefField(
                              s,
                              key,
                              "render",
                              e.target.value,
                            )
                          }
                        >
                          <option value="line">line</option>
                          <option value="bar">bar</option>
                          <option value="scatter">scatter</option>
                        </select>
                      </div>

                      <div className="prefsControl">
                        <div className="muted">Color</div>
                        <div className="row" style={{ gap: 8 }}>
                          <button
                            type="button"
                            className="swatch"
                            onClick={() =>
                              setChartPrefsPickerKey(`series:${s}:${key}`)
                            }
                            title="Pick series color (blank = inherit base)"
                          >
                            <span
                              className="swatchDot"
                              style={{
                                background:
                                  colorVal ||
                                  chartPrefsDraft.colors?.[s] ||
                                  COLORS[s],
                              }}
                            />
                            <span className="mono">
                              {colorVal ? colorVal : "inherit"}
                            </span>
                          </button>
                          <button
                            type="button"
                            className="btn"
                            onClick={() =>
                              updateChartPrefField(s, key, "color", "")
                            }
                            title="Clear override (inherit base stream color)"
                          >
                            Clear
                          </button>
                        </div>
                      </div>

                      {render === "line" ? (
                        <>
                          <div className="prefsControl">
                            <div className="muted">Width</div>
                            <input
                              className="inp"
                              type="number"
                              step="0.5"
                              value={pref.width ?? ""}
                              placeholder="auto"
                              onChange={(e) =>
                                updateChartPrefField(
                                  s,
                                  key,
                                  "width",
                                  e.target.value,
                                )
                              }
                            />
                          </div>

                          <div className="prefsControl">
                            <div className="muted">Dash</div>
                            <select
                              className="inp"
                              value={pref.dash || "solid"}
                              onChange={(e) =>
                                updateChartPrefField(
                                  s,
                                  key,
                                  "dash",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="solid">solid</option>
                              <option value="dashed">dashed</option>
                              <option value="dotted">dotted</option>
                            </select>
                          </div>

                          <div className="prefsControl">
                            <div className="muted">Opacity</div>
                            <input
                              className="inp"
                              type="number"
                              step="0.05"
                              min="0"
                              max="1"
                              value={pref.opacity ?? ""}
                              placeholder="auto"
                              onChange={(e) =>
                                updateChartPrefField(
                                  s,
                                  key,
                                  "opacity",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </>
                      ) : null}

                      {render === "bar" ? (
                        <>
                          <div className="prefsControl">
                            <div className="muted">Bar width</div>
                            <input
                              className="inp"
                              type="number"
                              step="1"
                              value={pref.barWidth ?? ""}
                              placeholder="auto"
                              onChange={(e) =>
                                updateChartPrefField(
                                  s,
                                  key,
                                  "barWidth",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                          <div className="prefsControl">
                            <div className="muted">Opacity</div>
                            <input
                              className="inp"
                              type="number"
                              step="0.05"
                              min="0"
                              max="1"
                              value={pref.opacity ?? ""}
                              placeholder="auto"
                              onChange={(e) =>
                                updateChartPrefField(
                                  s,
                                  key,
                                  "opacity",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </>
                      ) : null}

                      {render === "scatter" ? (
                        <>
                          <div className="prefsControl">
                            <div className="muted">Symbol</div>
                            <select
                              className="inp"
                              value={pref.symbol || "circle"}
                              onChange={(e) =>
                                updateChartPrefField(
                                  s,
                                  key,
                                  "symbol",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="circle">circle</option>
                              <option value="triangle">triangle</option>
                              <option value="rect">rect</option>
                              <option value="diamond">diamond</option>
                              <option value="pin">pin</option>
                            </select>
                          </div>

                          <div className="prefsControl">
                            <div className="muted">Size</div>
                            <input
                              className="inp"
                              type="number"
                              step="1"
                              value={pref.symbolSize ?? ""}
                              placeholder="auto"
                              onChange={(e) =>
                                updateChartPrefField(
                                  s,
                                  key,
                                  "symbolSize",
                                  e.target.value,
                                )
                              }
                            />
                          </div>

                          <div className="prefsControl">
                            <div className="muted">Opacity</div>
                            <input
                              className="inp"
                              type="number"
                              step="0.05"
                              min="0"
                              max="1"
                              value={pref.opacity ?? ""}
                              placeholder="auto"
                              onChange={(e) =>
                                updateChartPrefField(
                                  s,
                                  key,
                                  "opacity",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

            {chartPrefsPickerKey ? (
              <div className="pickerPop">
                <div
                  className="row"
                  style={{ justifyContent: "space-between", marginBottom: 8 }}
                >
                  <div className="mono" style={{ fontSize: 12 }}>
                    {chartPrefsPickerKey}
                  </div>
                  <button
                    className="btn"
                    onClick={() => setChartPrefsPickerKey(null)}
                  >
                    Done
                  </button>
                </div>
                <SketchPicker
                  color={getPickerColor(chartPrefsDraft, chartPrefsPickerKey)}
                  onChange={(c) => handlePickerChange(c?.hex)}
                />
              </div>
            ) : null}

            {chartsPrefError ? (
              <div className="warnBox">{chartsPrefError}</div>
            ) : null}
          </div>
        )}
      </Modal>
      <Modal
        open={econModalOpen}
        title={`Econ & Decision Metrics • ${econBundle?.selectedName || "Base"} scenario`}
        onClose={() => setEconModalOpen(false)}
        footer={
          <>
            <div className="actions">
              <span
                className="pill"
                title="Derived from royalty + WI (or NRI override)"
              >
                NRI: {pct.format(clamp01(derivedNRI))}
              </span>
              <span
                className="pill"
                title="Working interest used in calculations"
              >
                WI: {pct.format(clamp01(effectiveWI))}
              </span>
              <span className="pill" title="Discount rate used for PV">
                Disc: {pct.format(clamp01(econ.discountRateAnnual))}
              </span>
            </div>
            <div className="actions">
              <button
                className="btn"
                onClick={handleExportEconCSV}
                disabled={!econSelected}
              >
                Export Econ CSV
              </button>
              <button
                className="btn primary"
                onClick={() => setEconModalOpen(false)}
              >
                Done
              </button>
            </div>
          </>
        }
      >
        <div className="tabs">
          <button
            className={`tab ${econTab === "summary" ? "on" : ""}`}
            onClick={() => setEconTab("summary")}
          >
            Summary
          </button>
          <button
            className={`tab ${econTab === "assumptions" ? "on" : ""}`}
            onClick={() => setEconTab("assumptions")}
          >
            Assumptions
          </button>
          <button
            className={`tab ${econTab === "table" ? "on" : ""}`}
            onClick={() => setEconTab("table")}
          >
            Table
          </button>

          <div style={{ flex: 1 }} />

          <label style={{ fontSize: 12, color: "var(--muted)" }}>
            Scenario
          </label>
          <select
            value={econScenarioKey}
            onChange={(e) => setEconScenarioKey(e.target.value)}
            style={{
              background: "var(--bg)",
              border: "1px solid var(--border)",
              color: "var(--text)",
              borderRadius: 10,
              padding: "6px 8px",
            }}
          >
            {ECON_SCENARIOS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        {econTab === "summary" && (
          <>
            <div className="grid2">
              <div className="panelCard">
                <h5>
                  <span className="h5Row">
                    Decision
                    <InfoTip text={ECON_TIPS.sections.decision} />
                  </span>
                </h5>
                <div
                  className="kpiBig"
                  style={{
                    color:
                      econSelected?.decision === "Shut-in candidate"
                        ? "#ef4444"
                        : econSelected?.decision === "Monitor"
                          ? "#f59e0b"
                          : undefined,
                  }}
                >
                  {econSelected?.decision || "—"}
                </div>
                <div className="kpiRow">
                  <span>
                    {econSelected?.decisionDetail ||
                      "Run overlay to compute econ."}
                  </span>
                </div>

                <div className="divider" />

                <div className="kpiRow">
                  <span className="kpiLabel">
                    First negative CF
                    <InfoTip text={ECON_TIPS.summary.firstNegDate} />
                  </span>
                  <b>{econSelected?.firstNegDate || "—"}</b>
                </div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    Economic limit date
                    <InfoTip text={ECON_TIPS.summary.econLimitDate} />
                  </span>
                  <b>{econSelected?.econLimitDate || "—"}</b>
                </div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    Rolling avg trigger
                    <InfoTip text={ECON_TIPS.summary.rollingNegDate} />
                  </span>
                  <b>{econSelected?.rollingNegDate || "—"}</b>
                </div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    Consecutive neg trigger
                    <InfoTip text={ECON_TIPS.summary.consecutiveNegDate} />
                  </span>
                  <b>{econSelected?.consecutiveNegDate || "—"}</b>
                </div>
              </div>

              <div className="panelCard">
                <h5>
                  <span className="h5Row">
                    KPIs
                    <InfoTip text={ECON_TIPS.sections.kpis} />
                  </span>
                </h5>
                <div className="kpiBig">{money(econSelected?.pv, 0)}</div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    PV (discounted stream)
                    <InfoTip text={ECON_TIPS.summary.pv} />
                  </span>
                  <b style={{ color: signColor(econSelected?.pv, dark) }}>
                    {econSelected?.pv != null ? "" : "—"}
                  </b>
                </div>

                <div className="divider" />

                <div className="kpiRow">
                  <span className="kpiLabel">
                    Cumulative CF (window)
                    <InfoTip text={ECON_TIPS.summary.totalCash} />
                  </span>
                  <b
                    style={{ color: signColor(econSelected?.totalCash, dark) }}
                  >
                    {money(econSelected?.totalCash, 0)}
                  </b>
                </div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    Netback ($/boe)
                    <InfoTip text={ECON_TIPS.summary.netback} />
                  </span>
                  <b style={{ color: signColor(econSelected?.netback, dark) }}>
                    {definedNum(econSelected?.netback)
                      ? money(econSelected.netback, 2)
                      : "—"}
                  </b>
                </div>

                <div className="kpiRow">
                  <span className="kpiLabel">
                    Total NGL (bbl)
                    <InfoTip text={ECON_TIPS.summary.totalNgl} />
                  </span>
                  <b>
                    {definedNum(econSelected?.totalNgl)
                      ? fmt.format(econSelected.totalNgl)
                      : "—"}
                  </b>
                </div>

                <div className="kpiRow">
                  <span className="kpiLabel">
                    Gross NGL ($)
                    <InfoTip text={ECON_TIPS.summary.totalGrossNgl} />
                  </span>
                  <b>
                    {definedNum(econSelected?.totalGrossNgl)
                      ? money(econSelected.totalGrossNgl, 0)
                      : "—"}
                  </b>
                </div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    LOE ($/boe)
                    <InfoTip text={ECON_TIPS.summary.loePerBoe} />
                  </span>
                  <b>
                    {definedNum(econSelected?.loePerBoe)
                      ? money(econSelected.loePerBoe, 2)
                      : "—"}
                  </b>
                </div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    Payout date
                    <InfoTip text={ECON_TIPS.summary.payoutDate} />
                  </span>
                  <b>{econSelected?.payoutDate || "—"}</b>
                </div>
              </div>
            </div>

            <div className="grid2" style={{ marginTop: 10 }}>
              <div className="panelCard">
                <h5>
                  <span className="h5Row">
                    Breakevens & Sensitivities
                    <InfoTip
                      text={ECON_TIPS.sections.breakevens}
                      align="right"
                    />
                  </span>
                </h5>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    Breakeven Oil ($/bbl) for PV=0
                    <InfoTip
                      text={ECON_TIPS.breakevens.beOilPV0}
                      align="right"
                    />
                  </span>
                  <b>
                    {definedNum(econBundle?.breakevens?.beOilPV0)
                      ? fmt.format(econBundle.breakevens.beOilPV0)
                      : "—"}
                  </b>
                </div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    Breakeven Oil ($/bbl) for Min CF ≥ 0
                    <InfoTip
                      text={ECON_TIPS.breakevens.beOilMinCF0}
                      align="right"
                    />
                  </span>
                  <b>
                    {definedNum(econBundle?.breakevens?.beOilMinCF0)
                      ? fmt.format(econBundle.breakevens.beOilMinCF0)
                      : "—"}
                  </b>
                </div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    Max Fixed LOE ($/mo) for PV ≥ 0
                    <InfoTip
                      text={ECON_TIPS.breakevens.maxFixedLoePV0}
                      align="right"
                    />
                  </span>
                  <b>
                    {definedNum(econBundle?.breakevens?.maxFixedLoePV0)
                      ? fmt0.format(econBundle.breakevens.maxFixedLoePV0)
                      : "—"}
                  </b>
                </div>

                <div className="divider" />

                <div className="kpiRow">
                  <span className="kpiLabel">
                    Current Oil / Gas
                    <InfoTip text={ECON_TIPS.sections.prices} align="right" />
                  </span>
                  <b>
                    {fmt.format(
                      econBundle?.selectedEcon?.oilPrice || econ.oilPrice,
                    )}{" "}
                    /{" "}
                    {fmt.format(
                      econBundle?.selectedEcon?.gasPrice || econ.gasPrice,
                    )}
                  </b>
                </div>
                <div className="kpiRow">
                  <span className="kpiLabel">
                    Window
                    <InfoTip
                      text={ECON_TIPS.assumptions.unprofitableSoonDays}
                      align="right"
                    />
                  </span>
                  <b>
                    {econSelected?.days || econ.unprofitableSoonDays}d from{" "}
                  </b>
                </div>
              </div>

              <div className="panelCard">
                <h5>
                  <span className="h5Row">
                    Scenario compare
                    <InfoTip
                      text={`Scenario compare
• Each scenario applies multipliers to prices and (optionally) opex:
  - Base: 1.0×
  - Bear: lower prices + slightly higher opex
  - Bull: higher prices + slightly lower opex
• Decisions, PV, and Cum CF are recalculated for each.`}
                      align="right"
                    />
                  </span>
                </h5>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Scenario</th>
                      <th className="num">PV</th>
                      <th className="num">Cum CF</th>
                      <th>Decision</th>
                      <th>Limit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ECON_SCENARIOS.map((sc) => {
                      const r = econBundle?.scenarios?.[sc.key]?.result;
                      const dec = r?.decision || "—";
                      const cls =
                        dec === "Operate"
                          ? "good"
                          : dec === "Monitor"
                            ? "warn"
                            : dec === "Shut-in candidate"
                              ? "bad"
                              : "";
                      return (
                        <tr key={sc.key}>
                          <td>
                            <b>{sc.name}</b>
                            {econScenarioKey === sc.key ? (
                              <span
                                style={{ marginLeft: 8 }}
                                className={`pill ${cls}`}
                              >
                                selected
                              </span>
                            ) : null}
                          </td>
                          <td
                            className="num"
                            style={{ color: signColor(r?.pv, dark) }}
                          >
                            {money(r?.pv, 0)}
                          </td>
                          <td
                            className="num"
                            style={{ color: signColor(r?.totalCash, dark) }}
                          >
                            {money(r?.totalCash, 0)}
                          </td>
                          <td>
                            <span className={`pill ${cls}`}>{dec}</span>
                          </td>
                          <td>{r?.econLimitDate || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="divider" />

                {econChartOption ? (
                  <ReactECharts
                    style={{
                      height: 240,
                      width: "100%",
                      border: `1px solid var(--border)`,
                      borderRadius: 12,
                    }}
                    option={econChartOption}
                    notMerge={true}
                    lazyUpdate={true}
                  />
                ) : (
                  <div className="subtitle">
                    Run overlay to view econ chart.
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {econTab === "assumptions" && (
          <>
            <div className="grid2">
              <div className="panelCard">
                <h5>
                  <span className="h5Row">
                    Prices
                    <InfoTip text={ECON_TIPS.sections.prices} />
                  </span>
                </h5>

                <div className="econGrid">
                  <label>
                    <span className="econKpi econKpiLabel">
                      Oil $/bbl
                      <InfoTip text={ECON_TIPS.assumptions.oilPrice} />
                    </span>
                    <input
                      type="number"
                      value={econ.oilPrice}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          oilPrice: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Gas $/mcf
                      <InfoTip text={ECON_TIPS.assumptions.gasPrice} />
                    </span>
                    <input
                      type="number"
                      value={econ.gasPrice}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          gasPrice: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  {/* NGL (optional) */}
                  <label>
                    <span className="econKpi econKpiLabel">
                      NGL yield (bbl/mcf)
                      <InfoTip text={ECON_TIPS.assumptions.nglYieldBblPerMcf} />
                    </span>
                    <input
                      type="number"
                      step="0.001"
                      value={econ.nglYieldBblPerMcf}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          nglYieldBblPerMcf: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      NGL price mode
                      <InfoTip text={ECON_TIPS.assumptions.nglMode} />
                    </span>
                    <select
                      value={econ.nglMode}
                      onChange={(e) =>
                        setEcon((x) => ({ ...x, nglMode: e.target.value }))
                      }
                      style={{ width: "100%" }}
                    >
                      <option value="pct_wti">% of WTI</option>
                      <option value="per_gal">$ / gallon</option>
                    </select>
                  </label>

                  {String(econ.nglMode || "pct_wti") === "per_gal" ? (
                    <label>
                      <span className="econKpi econKpiLabel">
                        NGL $/gal
                        <InfoTip text={ECON_TIPS.assumptions.nglPricePerGal} />
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        value={econ.nglPricePerGal}
                        onChange={(e) =>
                          setEcon((x) => ({
                            ...x,
                            nglPricePerGal: parseFloat(e.target.value || "0"),
                          }))
                        }
                        style={{ width: "100%" }}
                      />
                    </label>
                  ) : (
                    <label>
                      <span className="econKpi econKpiLabel">
                        NGL % of WTI
                        <InfoTip text={ECON_TIPS.assumptions.nglPctOfWti} />
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        value={econ.nglPctOfWti}
                        onChange={(e) =>
                          setEcon((x) => ({
                            ...x,
                            nglPctOfWti: parseFloat(e.target.value || "0"),
                          }))
                        }
                        style={{ width: "100%" }}
                      />
                    </label>
                  )}

                  <label>
                    <span className="econKpi econKpiLabel">
                      Oil diff ($/bbl)
                      <InfoTip text={ECON_TIPS.assumptions.oilDiff} />
                    </span>
                    <input
                      type="number"
                      value={econ.oilDiff}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          oilDiff: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Gas diff ($/mcf)
                      <InfoTip text={ECON_TIPS.assumptions.gasDiff} />
                    </span>
                    <input
                      type="number"
                      value={econ.gasDiff}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          gasDiff: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Gas shrink
                      <InfoTip text={ECON_TIPS.assumptions.gasShrink} />
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      value={econ.gasShrink}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          gasShrink: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Downtime %
                      <InfoTip text={ECON_TIPS.assumptions.downtimePct} />
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      value={econ.downtimePct}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          downtimePct: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                </div>

                <div className="divider" />

                <h5>
                  <span className="h5Row">
                    Marketing / Transport
                    <InfoTip text={ECON_TIPS.sections.marketing} />
                  </span>
                </h5>

                <div className="econGrid">
                  <label>
                    <span className="econKpi econKpiLabel">
                      Oil mkt ($/bbl)
                      <InfoTip
                        text={ECON_TIPS.assumptions.oilMarketingPerBbl}
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.oilMarketingPerBbl}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          oilMarketingPerBbl: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Gas mkt ($/mcf)
                      <InfoTip
                        text={ECON_TIPS.assumptions.gasMarketingPerMcf}
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.gasMarketingPerMcf}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          gasMarketingPerMcf: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Oil trans ($/bbl)
                      <InfoTip
                        text={ECON_TIPS.assumptions.oilTransportPerBbl}
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.oilTransportPerBbl}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          oilTransportPerBbl: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Gas trans ($/mcf)
                      <InfoTip
                        text={ECON_TIPS.assumptions.gasTransportPerMcf}
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.gasTransportPerMcf}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          gasTransportPerMcf: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  {/* NGL moved here (still marketing-ish), keep the same fields */}
                  <label>
                    <span className="econKpi econKpiLabel">
                      NGL yield (bbl/mcf)
                      <InfoTip text={ECON_TIPS.assumptions.nglYieldBblPerMcf} />
                    </span>
                    <input
                      type="number"
                      step="0.001"
                      value={econ.nglYieldBblPerMcf}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          nglYieldBblPerMcf: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      NGL price mode
                      <InfoTip text={ECON_TIPS.assumptions.nglMode} />
                    </span>{" "}
                    <select
                      value={econ.nglMode}
                      onChange={(e) =>
                        setEcon((x) => ({ ...x, nglMode: e.target.value }))
                      }
                      style={{ width: "100%" }}
                    >
                      <option value="pct_wti">% of WTI</option>
                      <option value="per_gal">$ / gallon</option>
                    </select>
                  </label>

                  {String(econ.nglMode || "pct_wti") === "per_gal" ? (
                    <label>
                      <span className="econKpi econKpiLabel">NGL $/gal</span>
                      <input
                        type="number"
                        step="0.01"
                        value={econ.nglPricePerGal}
                        onChange={(e) =>
                          setEcon((x) => ({
                            ...x,
                            nglPricePerGal: parseFloat(e.target.value || "0"),
                          }))
                        }
                        style={{ width: "100%" }}
                      />
                    </label>
                  ) : (
                    <label>
                      <span className="econKpi econKpiLabel">NGL % of WTI</span>
                      <input
                        type="number"
                        step="0.01"
                        value={econ.nglPctOfWti}
                        onChange={(e) =>
                          setEcon((x) => ({
                            ...x,
                            nglPctOfWti: parseFloat(e.target.value || "0"),
                          }))
                        }
                        style={{ width: "100%" }}
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="panelCard">
                <h5>
                  <span className="h5Row">
                    Ownership & Taxes
                    <InfoTip
                      text={ECON_TIPS.sections.ownership}
                      align="right"
                    />
                  </span>
                </h5>

                <div className="econGrid">
                  <label>
                    <span className="econKpi econKpiLabel">
                      Royalty
                      <InfoTip
                        text={ECON_TIPS.assumptions.royalty}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      value={econ.royalty}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          royalty: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Working Interest (WI)
                      <InfoTip
                        text={ECON_TIPS.assumptions.workingInterest}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      value={econ.workingInterest}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          workingInterest: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      NRI override (optional)
                      <InfoTip
                        text={ECON_TIPS.assumptions.nriOverride}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      value={econ.nriOverride}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          nriOverride: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <div className="econKpi" style={{ alignSelf: "end" }}>
                    Derived NRI: <b>{pct.format(clamp01(derivedNRI))}</b>
                  </div>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Oil sev tax
                      <InfoTip
                        text={ECON_TIPS.assumptions.oilSevTax}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      step="0.001"
                      value={econ.oilSevTax}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          oilSevTax: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Gas sev tax
                      <InfoTip
                        text={ECON_TIPS.assumptions.gasSevTax}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      step="0.001"
                      value={econ.gasSevTax}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          gasSevTax: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Ad valorem tax
                      <InfoTip
                        text={ECON_TIPS.assumptions.adValoremTax}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      step="0.001"
                      value={econ.adValoremTax}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          adValoremTax: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                </div>

                <div className="divider" />

                <h5>
                  <span className="h5Row">
                    Opex (LOE)
                    <InfoTip text={ECON_TIPS.sections.opex} align="right" />
                  </span>
                </h5>

                <div className="econGrid">
                  {/* Advanced toggle row */}
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        fontSize: 12,
                        color: "var(--muted)",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={showAdvancedOpex}
                        onChange={(e) => setShowAdvancedOpex(e.target.checked)}
                      />
                      Show advanced LOE inputs (chemicals / electricity / base
                      maint)
                    </label>
                  </div>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Variable Oil $/bbl
                    </span>
                    <input
                      type="number"
                      value={econ.varOilPerBbl}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          varOilPerBbl: parseFloat(e.target.value || "0"),
                        }))
                      }
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Variable Gas $/mcf
                    </span>
                    <input
                      type="number"
                      value={econ.varGasPerMcf}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          varGasPerMcf: parseFloat(e.target.value || "0"),
                        }))
                      }
                    />
                  </label>
                  <label>
                    <span className="econKpi econKpiLabel">
                      Fixed LOE $/mo
                      <InfoTip
                        text={ECON_TIPS.assumptions.fixedLoePerMonth}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.fixedLoePerMonth}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          fixedLoePerMonth: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Var LOE $/boe
                      <InfoTip
                        text={ECON_TIPS.assumptions.varLoePerBoe}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.varLoePerBoe}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          varLoePerBoe: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Water disposal $/bbl
                      <InfoTip
                        text={ECON_TIPS.assumptions.waterDisposalPerBbl}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.waterDisposalPerBbl}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          waterDisposalPerBbl: parseFloat(
                            e.target.value || "0",
                          ),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Overhead $/mo
                      <InfoTip
                        text={ECON_TIPS.assumptions.overheadPerMonth}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.overheadPerMonth}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          overheadPerMonth: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  {showAdvancedOpex ? (
                    <>
                      <label>
                        <span className="econKpi econKpiLabel">
                          Chemicals $/boe
                          <InfoTip
                            text={ECON_TIPS.assumptions.chemicalsPerBoe}
                            align="right"
                          />
                        </span>
                        <input
                          type="number"
                          value={econ.chemicalsPerBoe}
                          onChange={(e) =>
                            setEcon((x) => ({
                              ...x,
                              chemicalsPerBoe: parseFloat(
                                e.target.value || "0",
                              ),
                            }))
                          }
                          style={{ width: "100%" }}
                        />
                      </label>

                      <label>
                        <span className="econKpi econKpiLabel">
                          Electricity $/boe
                          <InfoTip
                            text={ECON_TIPS.assumptions.electricityPerBoe}
                            align="right"
                          />
                        </span>
                        <input
                          type="number"
                          value={econ.electricityPerBoe}
                          onChange={(e) =>
                            setEcon((x) => ({
                              ...x,
                              electricityPerBoe: parseFloat(
                                e.target.value || "0",
                              ),
                            }))
                          }
                          style={{ width: "100%" }}
                        />
                      </label>

                      <label>
                        <span className="econKpi econKpiLabel">
                          Base maint $/mo
                          <InfoTip
                            text={ECON_TIPS.assumptions.baseMaintPerMonth}
                            align="right"
                          />
                        </span>
                        <input
                          type="number"
                          value={econ.baseMaintPerMonth}
                          onChange={(e) =>
                            setEcon((x) => ({
                              ...x,
                              baseMaintPerMonth: parseFloat(
                                e.target.value || "0",
                              ),
                            }))
                          }
                          style={{ width: "100%" }}
                        />
                      </label>
                    </>
                  ) : null}

                  <label>
                    <span className="econKpi econKpiLabel">
                      Low-rate threshold (boe/d)
                      <InfoTip
                        text={ECON_TIPS.assumptions.lowRateBoePerDayThreshold}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.lowRateBoePerDayThreshold}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          lowRateBoePerDayThreshold: parseFloat(
                            e.target.value || "0",
                          ),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Low-rate extra maint $/mo
                      <InfoTip
                        text={ECON_TIPS.assumptions.lowRateExtraMaintPerMonth}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.lowRateExtraMaintPerMonth}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          lowRateExtraMaintPerMonth: parseFloat(
                            e.target.value || "0",
                          ),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                </div>

                <div className="divider" />

                <h5>
                  <span className="h5Row">
                    Capex / Discount / Rules
                    <InfoTip text={ECON_TIPS.sections.capex} align="right" />
                  </span>
                </h5>

                <div className="econGrid">
                  <label>
                    <span className="econKpi econKpiLabel">
                      Capex upfront ($)
                      <InfoTip
                        text={ECON_TIPS.assumptions.capexUpfront}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.capexUpfront}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          capexUpfront: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Capex date (YYYY-MM-DD)
                      <InfoTip
                        text={ECON_TIPS.assumptions.capexDate}
                        align="right"
                      />
                    </span>
                    <input
                      type="text"
                      value={econ.capexDate}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          capexDate: e.target.value,
                        }))
                      }
                      placeholder="defaults to window start"
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Abandonment cost ($)
                      <InfoTip
                        text={ECON_TIPS.assumptions.abandonmentCost}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.abandonmentCost}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          abandonmentCost: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Abandonment date
                      <InfoTip
                        text={ECON_TIPS.assumptions.abandonmentDate}
                        align="right"
                      />
                    </span>
                    <input
                      type="text"
                      value={econ.abandonmentDate}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          abandonmentDate: e.target.value,
                        }))
                      }
                      placeholder="defaults to last day"
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Discount rate (annual)
                      <InfoTip
                        text={ECON_TIPS.assumptions.discountRateAnnual}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      value={econ.discountRateAnnual}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          discountRateAnnual: parseFloat(e.target.value || "0"),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Econ window (days)
                      <InfoTip
                        text={ECON_TIPS.assumptions.unprofitableSoonDays}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.unprofitableSoonDays}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          unprofitableSoonDays: parseFloat(
                            e.target.value || "60",
                          ),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Rolling avg days
                      <InfoTip
                        text={ECON_TIPS.assumptions.econLimitRollingDays}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.econLimitRollingDays}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          econLimitRollingDays: parseFloat(
                            e.target.value || "14",
                          ),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>

                  <label>
                    <span className="econKpi econKpiLabel">
                      Consecutive neg days
                      <InfoTip
                        text={ECON_TIPS.assumptions.econLimitConsecutiveNegDays}
                        align="right"
                      />
                    </span>
                    <input
                      type="number"
                      value={econ.econLimitConsecutiveNegDays}
                      onChange={(e) =>
                        setEcon((x) => ({
                          ...x,
                          econLimitConsecutiveNegDays: parseFloat(
                            e.target.value || "14",
                          ),
                        }))
                      }
                      style={{ width: "100%" }}
                    />
                  </label>
                </div>

                <div className="divider" />

                <div className="actions">
                  <button
                    className="btn"
                    onClick={() => {
                      setEcon({
                        // Prices & diffs
                        oilPrice: 70,
                        gasPrice: 3.0,
                        oilDiff: 3.0,
                        gasDiff: 0.3,
                        gasShrink: 0.05,

                        // NGL (optional)
                        nglMode: "pct_wti", // "pct_wti" | "per_gal"
                        nglPctOfWti: 0.45, // 0..1 (only if pct_wti)
                        nglPricePerGal: 0.0, // $/gal (only if per_gal)
                        nglYieldBblPerMcf: 0.0, // bbl NGL per mcf gas

                        // Midstream / marketing / transport
                        oilMarketingPerBbl: 0.0,
                        gasMarketingPerMcf: 0.0,
                        oilTransportPerBbl: 0.0,
                        gasTransportPerMcf: 0.0,

                        // Ownership & taxes
                        royalty: 0.2,
                        workingInterest: 1.0,
                        nriOverride: 0.0,
                        oilSevTax: 0.046,
                        gasSevTax: 0.075,
                        adValoremTax: 0.0,

                        // Opex (LOE)
                        fixedLoePerMonth: 4500,
                        varLoePerBoe: 2.25,
                        waterDisposalPerBbl: 1.25,
                        chemicalsPerBoe: 0.0,
                        electricityPerBoe: 0.0,
                        overheadPerMonth: 0.0,
                        baseMaintPerMonth: 0,
                        lowRateBoePerDayThreshold: 8,
                        lowRateExtraMaintPerMonth: 900,

                        // Capex timing
                        capexUpfront: 0,
                        capexDate: "",
                        abandonmentCost: 0,
                        abandonmentDate: "",

                        // Uptime factor
                        downtimePct: 0.0,

                        // Discounting
                        discountRateAnnual: 0.1,

                        // Units scaling
                        oilUnitFactor: 1.0,
                        gasUnitFactor: 1.0,
                        waterUnitFactor: 1.0,

                        // Decision rules / window
                        unprofitableSoonDays: 60,
                        econLimitRollingDays: 14,
                        econLimitConsecutiveNegDays: 14,
                      });
                    }}
                  >
                    Reset defaults
                  </button>
                </div>
              </div>
            </div>

            <div className="footnote">
              Econ uses <b>daily</b> series (actual + forecast p50), applies
              WI/royalty/taxes/LOE, then flags economic limit via
              rolling-average or consecutive-negative rules.
            </div>
          </>
        )}

        {econTab === "table" && (
          <>
            <div className="panelCard">
              <h5>
                <span className="h5Row">
                  Daily Econ Table (selected scenario)
                  <InfoTip
                    text={`Daily econ table (columns)
• Owner Rev: (GrossRev − Royalty − Taxes) × WI
• Opex: LOE bucket × WI
• Capex: one-time costs on specified dates × WI
• Cashflow: OwnerRev − Opex − Capex
• Cum CF: running sum of cashflow
• PV: discounted cashflow using annual discount rate`}
                    align="right"
                  />
                </span>
              </h5>

              {!econSelected?.rows?.length ? (
                <div className="subtitle">
                  Run overlay to compute econ table.
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th className="num">Oil</th>
                        <th className="num">Gas</th>
                        <th className="num">Water</th>
                        <th className="num">BOE</th>
                        <th className="num">Owner Rev</th>
                        <th className="num">Opex</th>
                        <th className="num">Capex</th>
                        <th className="num">Cashflow</th>
                        <th className="num">Cum CF</th>
                        <th className="num">PV</th>
                        <th className="num">NGL</th>
                        <th className="num">NGL $</th>{" "}
                      </tr>
                    </thead>
                    <tbody>
                      {econSelected.rows.map((r) => (
                        <tr key={r.date}>
                          <td>
                            <b>{r.date}</b>
                          </td>
                          <td className="num">
                            {definedNum(r.oil) ? fmt.format(r.oil) : "—"}
                          </td>
                          <td className="num">
                            {definedNum(r.gas) ? fmt.format(r.gas) : "—"}
                          </td>
                          <td className="num">
                            {definedNum(r.water) ? fmt.format(r.water) : "—"}
                          </td>
                          <td className="num">
                            {definedNum(r.boe) ? fmt.format(r.boe) : "—"}
                          </td>
                          <td className="num">{money(r.ownerRev, 0)}</td>
                          <td className="num">{money(r.opex, 0)}</td>
                          <td className="num">{money(r.capex, 0)}</td>
                          <td
                            className="num"
                            style={{ color: signColor(r.cashflow, dark) }}
                          >
                            {money(r.cashflow, 0)}
                          </td>
                          <td
                            className="num"
                            style={{ color: signColor(r.cumCashflow, dark) }}
                          >
                            {money(r.cumCashflow, 0)}
                          </td>
                          <td
                            className="num"
                            style={{ color: signColor(r.pv, dark) }}
                          >
                            {money(r.pv, 0)}
                          </td>
                          <td className="num">
                            {definedNum(r.ngl) ? fmt.format(r.ngl) : "—"}
                          </td>
                          <td className="num">{money(r.grossNgl, 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </Modal>

      <div className="layout">
        <div className="panel">
          <h3>{tagsActive ? "Tag view" : "Lease"}</h3>

          {tagsActive ? (
            <div className="footnote">
              Tag aggregation is active. Lease selection is hidden/ignored.
            </div>
          ) : (
            <div className="row">
              <select
                value={lease}
                onChange={(e) => setLease(e.target.value)}
                style={{ width: "100%" }}
                disabled={ltLoading}
              >
                <option value="~ALL~">~ALL~</option>
                {leaseOptions.map((l) => (
                  <option key={l.LeaseID} value={l.LeaseID}>
                    {l.LeaseName}
                  </option>
                ))}
              </select>
            </div>
          )}

          <h3>
            <span className="h5Row">
              Tags <InfoTip text={TAG_DESELECT_TIP} />
            </span>
          </h3>
          <div className="row">
            <select multiple value={selectedTags} onChange={onTagsChange}>
              {tagOptions.map((t) => (
                <option key={t.TagID} value={t.TagID}>
                  {t.TagDesc}
                </option>
              ))}
            </select>
          </div>
          {ltError ? <div className="footnote err">{ltError}</div> : null}
          {ltLoading ? (
            <div className="footnote">Loading leases/tags…</div>
          ) : null}

          <h3>Snapshot</h3>
          <div className="stampBox">
            <div className="stampHeader">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="stampLabel">Created at</div>
                <div className="stampValue">
                  {data?.meta?.created_at || "—"}
                </div>
              </div>
            </div>
            <div className="stampSubRow">
              <span className="pill" title="Last run at">
                Run: {runAt || "—"}
              </span>
              <span className="pill" title="Backend response time">
                Latency: {latencyMs != null ? `${latencyMs}ms` : "—"}
              </span>
            </div>
          </div>

          <h3>Date range</h3>
          <div className="row">
            <label style={{ width: "100%" }}>
              From (read-only)
              <div>
                <input
                  type="date"
                  value={actualDomain.from || ""}
                  disabled
                  style={{ width: "100%" }}
                />
              </div>
            </label>

            <label style={{ width: "100%" }}>
              Thru (read-only)
              <div>
                <input
                  type="date"
                  value={actualDomain.thru || ""}
                  disabled
                  style={{ width: "100%" }}
                />
              </div>
            </label>
          </div>

          <h3>Forecast</h3>
          <div className="row">
            <label>
              Horizon (days)
              <div>
                <input
                  type="number"
                  min={7}
                  max={365}
                  value={horizon}
                  onChange={(e) =>
                    setHorizon(parseInt(e.target.value || "60", 10))
                  }
                  style={{ width: 120 }}
                />
              </div>
            </label>
          </div>

          <h3>Econ quick prices</h3>
          <div className="row">
            <label>
              Oil $/bbl
              <div>
                <input
                  type="number"
                  value={econ.oilPrice}
                  onChange={(e) =>
                    setEcon((x) => ({
                      ...x,
                      oilPrice: parseFloat(e.target.value || "0"),
                    }))
                  }
                  style={{ width: 120 }}
                />
              </div>
            </label>

            <label>
              Gas $/mcf
              <div>
                <input
                  type="number"
                  value={econ.gasPrice}
                  onChange={(e) =>
                    setEcon((x) => ({
                      ...x,
                      gasPrice: parseFloat(e.target.value || "0"),
                    }))
                  }
                  style={{ width: 120 }}
                />
              </div>
            </label>
          </div>

          <h3>Streams</h3>
          <div className="chips">
            {STREAMS.map((s) => (
              <div
                key={s}
                className={`chip ${streams.includes(s) ? "on" : ""}`}
                onClick={() => toggleStream(s)}
                title="Toggle stream"
              >
                {s}
              </div>
            ))}
          </div>

          <h3>Chart options</h3>
          <div className="row">
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={logScale}
                onChange={(e) => setLogScale(e.target.checked)}
              />
              Log scale
            </label>

            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={showBands}
                onChange={(e) => setShowBands(e.target.checked)}
              />
              Bands (p10–p90)
            </label>
          </div>

          <div className="row">
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={showMA}
                onChange={(e) => setShowMA(e.target.checked)}
              />
              Moving avg
            </label>

            <label>
              Window
              <div>
                <input
                  type="number"
                  min={2}
                  max={60}
                  value={maWindow}
                  onChange={(e) =>
                    setMaWindow(
                      Math.max(
                        2,
                        Math.min(60, parseInt(e.target.value || "7", 10)),
                      ),
                    )
                  }
                  style={{ width: 90 }}
                  disabled={!showMA}
                />
              </div>
            </label>
          </div>

          <div className="row">
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={showOutliers}
                onChange={(e) => setShowOutliers(e.target.checked)}
              />
              Outliers
            </label>

            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={zeroAsNull}
                onChange={(e) => setZeroAsNull(e.target.checked)}
              />
              0 → null
            </label>
          </div>

          <div className="row">
            <label>
              Cumulative
              <div>
                <select
                  value={cumulativeMode}
                  onChange={(e) => setCumulativeMode(e.target.value)}
                >
                  <option value="off">off</option>
                  <option value="on">on</option>
                </select>
              </div>
            </label>
          </div>

          <div className="divider" />

          <div className="actions">
            <button className="btn primary" onClick={load} disabled={loading}>
              {loading ? "Running…" : "Run overlay"}
            </button>
            <span className="pill" title="Backend response time">
              Latency: {latencyMs != null ? `${latencyMs}ms` : "—"}
            </span>
            <span className="pill" title="Last run at">
              Run: {runAt || "—"}
            </span>
          </div>

          {error ? <div className="footnote err">{error}</div> : null}
        </div>

        <div className="content">
          <div className="cards">
            {cards.map((c) => {
              const unit = unitsByStream[c.stream] || "";
              const lastText = definedNum(c.last)
                ? withUnits(c.last, unit)
                : "—";
              const avgText = definedNum(c.avg7)
                ? withUnits(c.avg7, unit)
                : "—";
              const deltaText = definedNum(c.delta) ? pct.format(c.delta) : "—";
              const nextText = definedNum(c.next7)
                ? withUnits(c.next7, unit)
                : "—";
              const deltaCol = signColor(
                definedNum(c.delta) ? (c.delta >= 0 ? 1 : -1) : null,
                dark,
              );

              return (
                <div className="card" key={c.stream}>
                  <h4>{c.stream}</h4>
                  <div className="val">{lastText}</div>
                  <div className="sub">
                    7d avg: <b>{avgText}</b> • Δ:{" "}
                    <b style={{ color: deltaCol }}>{deltaText}</b>
                  </div>
                  <div className="sub">
                    Next 7 (p50 sum): <b>{nextText}</b>
                  </div>
                </div>
              );
            })}
          </div>

          {loading && !data ? (
            <div className="skeleton">Loading overlay…</div>
          ) : (
            <ReactECharts
              ref={chartRef}
              option={option}
              notMerge={true}
              lazyUpdate={true}
              style={{ height: 560, width: "100%", borderRadius: 12 }}
            />
          )}

          <div className="footnote">
            Tip: The URL stays shareable (token/grant preserved). Use{" "}
            <b>Econ & Decisions</b> for PV + shut-in signals + breakevens.
          </div>
        </div>
      </div>
    </div>
    </ForecastDashboardContext.Provider>
  );
}
